import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../../Components/Header";
import Footer from "../../Components/Footer";
import TagManager from "react-gtm-module";
import FloatingDownloadBanner from "./FloatingDownloadBanner";


const FreeStartLanding = () => {
  // 스크롤 감지 상태
  const [scrolled, setScrolled] = useState(false);

  // 간단 문의 폼 상태
  const [quickForm, setQuickForm] = useState({
    name: "",
    contact: "", // 이메일 또는 전화번호
    company: "",
    message: "",
    contactMethod: "email",
    privacy: false,
  });

  // 로딩 및 성공 메시지 상태
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // 스크롤 이벤트 핸들러
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // 폼 입력 핸들러
  const handleQuickFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setQuickForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
      // 연락 방법이 변경되면 contact 필드 초기화
      ...(name === "contactMethod" ? { contact: "" } : {}),
    }));
  };

  // 폼 제출 핸들러 - 비동기 개선 버전
  const handleQuickFormSubmit = async (e) => {
    e.preventDefault();

    // 유효성 검사
    const newErrors = {};
    if (!quickForm.name.trim()) newErrors.name = "이름을 입력해주세요.";
    if (!quickForm.contact.trim())
      newErrors.contact =
        quickForm.contactMethod === "email"
          ? "이메일을 입력해주세요."
          : "전화번호를 입력해주세요.";
    if (!quickForm.privacy)
      newErrors.privacy = "개인정보처리방침에 동의해주세요.";

    // 이메일 형식 검사
    if (
      quickForm.contactMethod === "email" &&
      quickForm.contact &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(quickForm.contact)
    ) {
      newErrors.contact = "유효한 이메일 주소를 입력해주세요.";
    }

    // 전화번호 형식 검사
    if (
      quickForm.contactMethod !== "email" &&
      quickForm.contact &&
      !/^[\d\-\+\(\)]{8,20}$/.test(quickForm.contact)
    ) {
      newErrors.contact = "유효한 전화번호를 입력해주세요.";
    }

    if (Object.keys(newErrors).length > 0) {
      console.log("Validation errors:", newErrors);
      return;
    }

    // 즉시 성공 메시지 표시 (비동기 처리)
    setSuccessMessage({
      title: "무료 체험 문의가 접수되었습니다!",
      message: `24시간 내에 담당자가 ${quickForm.contact}(으)로 연락드리겠습니다.`,
    });

    // 폼 초기화
    const formDataCopy = { ...quickForm }; // 데이터 복사
    setQuickForm({
      name: "",
      contact: "",
      company: "",
      message: "",
      contactMethod: "email",
      privacy: false,
    });

    // GTM 이벤트 즉시 전송
    window.dataLayer = window.dataLayer || [];
    TagManager.dataLayer({
      dataLayer: {
        event: "form_submission_success",
        form_name: "free_trial_form",
        user_email:
          formDataCopy.contactMethod === "email" ? formDataCopy.contact : "",
        contact_method: formDataCopy.contactMethod,
        form_type: "free_trial",
      },
    });

    // 백그라운드에서 Google Sheets 전송 (실패해도 사용자 경험에 영향 없음)
    sendToGoogleSheets(formDataCopy).catch((error) => {
      console.error("Failed to save to Google Sheets:", error);
    });

    // 스크롤 복원
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Google Sheets 전송 함수 (분리)
  const sendToGoogleSheets = async (formData) => {
    const SCRIPT_URL =
      "https://script.google.com/macros/s/AKfycbzln4YMAbLsLxbpSvlEgN9D3er1am0YosIubXqnTV5UCzN4PWLYpg3CPO0MzQgB3kHS/exec";

    const submitData = {
      companyName: formData.company || "",
      name: formData.name,
      position: "",
      email: formData.contactMethod === "email" ? formData.contact : "",
      phone: formData.contactMethod !== "email" ? formData.contact : "",
      companySize: "",
      industry: "",
      preferredContact: formData.contactMethod,
      preferredTime: "",
      inquiry: formData.message || "무료 체험 신청",
      howDidYouHear: "free_start_page",
      formType: "free_trial",
      timestamp: new Date().toISOString(), // 타임스탬프 추가
    };

    console.log("Sending to Google Sheets:", submitData);

    // 재시도 로직 추가
    let retries = 3;
    while (retries > 0) {
      try {
        await fetch(SCRIPT_URL, {
          method: "POST",
          mode: "no-cors",
          redirect: "follow",
          body: JSON.stringify(submitData),
        });

        console.log("Successfully sent to Google Sheets");
        return;
      } catch (error) {
        retries--;
        if (retries === 0) throw error;

        // 재시도 전 대기
        await new Promise((resolve) => setTimeout(resolve, 1000));
        console.log(`Retrying... (${3 - retries}/3)`);
      }
    }
  };

  // 실패한 제출 재시도 (페이지 로드 시 실행)
  useEffect(() => {
    const retryFailedSubmissions = async () => {
      const failedSubmissions = JSON.parse(
        localStorage.getItem("failedSubmissions") || "[]"
      );

      if (failedSubmissions.length > 0) {
        console.log(
          `Retrying ${failedSubmissions.length} failed submissions...`
        );

        for (const submission of failedSubmissions) {
          try {
            await sendToGoogleSheets(submission.data);
            // 성공하면 제거
            const remaining = JSON.parse(
              localStorage.getItem("failedSubmissions") || "[]"
            );
            const updated = remaining.filter(
              (s) => s.timestamp !== submission.timestamp
            );
            localStorage.setItem("failedSubmissions", JSON.stringify(updated));
          } catch (error) {
            console.error("Retry failed:", error);
          }
        }
      }
    };

    // 5초 후 재시도
    setTimeout(retryFailedSubmissions, 5000);
  }, []);

  // 성공 메시지 닫기
  const closeSuccessMessage = () => {
    setSuccessMessage(null);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="h-16 bg-white"></div>

      {/* 성공/에러 메시지 모달 */}
      {successMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
            <div
              className={`text-center ${successMessage.isError ? "text-red-600" : "text-green-600"
                }`}
            >
              <svg
                className={`w-16 h-16 mx-auto mb-4 ${successMessage.isError ? "text-red-600" : "text-green-600"
                  }`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                {successMessage.isError ? (
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                ) : (
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                )}
              </svg>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {successMessage.title}
              </h3>
              <p className="text-gray-600 mb-6">{successMessage.message}</p>
              <button
                onClick={closeSuccessMessage}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 히어로 섹션 - 모바일 최적화 */}
      <section className="pt-12 sm:pt-32 pb-10 sm:pb-20 bg-gradient-to-br from-black to-gray-900 text-white relative overflow-hidden">
        {/* 배경 효과 - 모바일에서 축소 */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10">
          <div className="absolute top-0 right-0 w-[150px] sm:w-[600px] h-[150px] sm:h-[600px] bg-blue-500 rounded-full blur-2xl sm:blur-3xl -translate-y-1/4 sm:-translate-y-1/3 translate-x-1/4 sm:translate-x-1/3"></div>
          <div className="absolute bottom-0 left-0 w-[150px] sm:w-[600px] h-[150px] sm:h-[600px] bg-blue-300 rounded-full blur-2xl sm:blur-3xl translate-y-1/4 sm:translate-y-1/3 -translate-x-1/4 sm:-translate-x-1/3"></div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* 배지 - 모바일에서 중앙 정렬 */}
          <div className="flex justify-center sm:justify-start mb-3 sm:mb-4">
            <div className="inline-block bg-blue-600 text-white text-xs sm:text-sm px-3 py-1 rounded-full">
              전환율 상승의 비밀
            </div>
          </div>

          {/* 헤드라인 - 모바일 텍스트 크기 조정 및 중앙 정렬 */}
          <h1 className="text-2xl sm:text-4xl lg:text-6xl font-bold mb-4 sm:mb-6 tracking-tight text-center sm:text-left">
            전환율을 높이는
            <br />
            <span className="text-blue-400">A/B 테스트, 히트맵, 퍼널, 세션 녹화 솔루션</span>
          </h1>

          {/* 서브 헤드라인 - 모바일 크기 조정 */}
          <p className="text-base sm:text-xl lg:text-2xl text-gray-300 mb-6 sm:mb-8 text-center sm:text-left">
            3분 만에 테스트 시작,
            <br className="sm:hidden" />
            개발자 없이 마케터가 직접
          </p>

          {/* 혜택 박스 - 모바일에서 간소화 + 컨설팅 추가 */}
          <div className="mt-6 sm:mt-8 mb-8 sm:mb-10">
            <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-6 border-l-4 border-blue-400">
              {/* 모바일: 더 간결한 버전 */}
              <div className="sm:hidden">
                <p className="text-base text-gray-100 mb-2">
                  A/B 테스트, 히트맵, 퍼널, 세션 녹화 기능{" "}
                  <strong className="text-yellow-300 text-lg">30일 무료</strong>{" "}
                </p>
                <p className="text-base text-gray-100 mb-2">
                  초기 설치와 설정{" "}
                  <strong className="text-yellow-300 text-lg">100% 지원</strong>
                </p>
                <p className="text-base text-gray-100">
                  전문가 A/B 테스트 컨설팅까지{" "}
                  <strong className="text-yellow-300 text-lg">모두 무료</strong>
                </p>
              </div>

              {/* 데스크톱: 원본 + 컨설팅 추가 */}
              <div className="hidden sm:block">
                <p className="text-lg sm:text-xl lg:text-2xl text-gray-100 mb-4">
                  A/B 테스트, 히트맵, 퍼널, 세션 녹화 기능을{" "}
                  <strong className="text-yellow-300 text-xl sm:text-2xl lg:text-3xl">
                    30일 동안 무료로 제공
                  </strong>
                  합니다.
                </p>
                <p className="text-lg sm:text-xl lg:text-2xl text-gray-100 mb-4">
                  초기 설치와 설정,{" "}
                  <strong className="text-yellow-300 text-xl sm:text-2xl lg:text-3xl">
                    100% 다 해드립니다.
                  </strong>
                </p>
                <p className="text-lg sm:text-xl lg:text-2xl text-gray-100">
                  전문가의 A/B 테스트 컨설팅까지{" "}
                  <strong className="text-yellow-300 text-xl sm:text-2xl lg:text-3xl">
                    모두 무료입니다.
                  </strong>

                </p>
              </div>
            </div>

            {/* 긴급성 배지 추가 */}
            <div className="flex justify-center mt-4">
              <div className="inline-flex items-center bg-red-100 text-red-700 px-5 py-3 rounded-full text-base sm:text-lg font-bold animate-pulse shadow-lg">
                <span className="mr-2 text-xl">⏰</span>
                선착순 50개 기업만
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* A/B 테스트란? - 초간단 버전 */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-6 sm:mb-8">
            {/* 모바일에서 강조 아이콘 */}
            <div className="sm:hidden mb-3">
              <span className="text-3xl">📊</span>
            </div>

            <h2 className="text-xl sm:text-3xl font-bold mb-3">
              <span className="block sm:inline">전환율 높이는 </span>
              <span className="block sm:inline">가장 확실한 방법 </span>
              <span className="text-blue-600 text-2xl sm:text-3xl block mt-1 sm:inline sm:mt-0">
                A/B 테스트
              </span>
            </h2>

            {/* 모바일: 시각적 설명 */}
            <div className="sm:hidden text-sm text-gray-600">
              <div className="flex items-center justify-center gap-2 mt-3 mb-2">
                <span className="bg-gray-100 px-3 py-1 rounded">50% A</span>
                <span>→</span>
                <span className="bg-blue-100 px-3 py-1 rounded font-bold">
                  50% B
                </span>
              </div>
              <p>
                고객 절반은 A를, 절반은 B를 보게 하고,
                <br /> 성과가 더 좋은 쪽을 선택
              </p>
            </div>
            {/* 데스크톱: 기존 설명 */}
            <p className="hidden sm:block text-lg text-gray-600">
              고객 절반은 A를, 절반은 B를 보게 하고, 성과가 더 좋은 쪽을
              선택합니다.
            </p>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
              <div className="text-center">
                <div className="bg-white p-4 sm:p-6 rounded-lg border-2 border-gray-200">
                  <div className="text-xs sm:text-sm text-gray-500 mb-2">
                    A안 (원본)
                  </div>
                  <button className="bg-blue-500 text-white px-4 sm:px-6 py-2 rounded text-xs sm:text-sm">
                    구매하기
                  </button>
                  <div className="mt-3 sm:mt-4 text-xl sm:text-2xl font-bold">
                    2.8%
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500">전환율</div>
                </div>
              </div>

              <div className="text-center">
                <div className="bg-white p-4 sm:p-6 rounded-lg border-2 border-green-400 relative">
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                    +25% 🎉
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 mb-2">
                    B안 (개선)
                  </div>
                  <button className="bg-green-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded text-xs sm:text-sm font-bold shadow-lg">
                    지금 구매하기 →
                  </button>
                  <div className="mt-3 sm:mt-4 text-xl sm:text-2xl font-bold text-green-600">
                    3.5%
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500">전환율</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 문제 제기 - 강력한 공감대 형성 */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          {/* 충격적인 통계로 시작 - 모바일 최적화 */}
          <div className="text-center mb-8 sm:mb-12">
            {/* 모바일: 원형 프로그레스 형태, 데스크탑: 기존 형태 */}
            <div className="sm:hidden mb-6">
              <div className="relative w-32 h-32 mx-auto">
                <svg className="transform -rotate-90 w-32 h-32">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="#fee2e2"
                    strokeWidth="12"
                    fill="none"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="#ef4444"
                    strokeWidth="12"
                    fill="none"
                    strokeDasharray="342 360"
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-4xl font-bold text-red-500">97%</span>
                  <span className="text-xs text-gray-500">이탈률</span>
                </div>
              </div>
              <p className="text-base font-medium text-gray-800 mt-3">
                방문자가 구매 없이 떠납니다
              </p>
              <p className="text-sm text-gray-500">
                업계 평균 전환율 3%의 현실
              </p>
            </div>

            {/* 데스크탑: 기존 레이아웃 */}
            <div className="hidden sm:block">
              <div className="inline-flex items-center gap-4 mb-6">
                <div className="text-5xl font-bold text-red-500">97%</div>
                <div className="text-left">
                  <div className="text-lg font-medium text-gray-800">
                    의 방문자가 구매 없이 떠납니다
                  </div>
                  <div className="text-sm text-gray-500">
                    업계 평균 전환율 3%의 현실
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">
              매일 <span className="text-red-600">수백만원의 매출 기회</span>를{' '}
              <br className="sm:hidden" />
              놓치고 있지 않으신가요?
            </h2>
          </div>

          {/* 공감 포인트 - 모바일: 스와이프 카드, 데스크탑: 기존 */}
          {/* 모바일 버전 */}
          <div className="sm:hidden mb-8">
            <div className="overflow-x-auto pb-2 -mx-4 px-4">
              <div className="flex gap-3" style={{ width: "max-content" }}>
                <div className="w-72 bg-white rounded-lg shadow-lg p-5 border border-gray-100">
                  <div className="flex items-start mb-3">
                    <div className="text-orange-500 mr-3 flex-shrink-0">
                      <svg
                        className="w-6 h-6"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <h3 className="text-base font-bold text-gray-800">
                      전환율 정체
                    </h3>
                  </div>

                  {/* 고객 인용구 - 강조 */}
                  <div className="bg-orange-50 border-l-4 border-orange-400 p-3 mb-3 rounded">
                    <p className="text-sm text-gray-800 font-medium italic">
                      "2.5%에서 멈춰있는데, 뭘 바꿔야 할지 모르겠어요"
                    </p>
                  </div>

                  {/* 설명 */}
                  <p className="text-sm text-gray-600">
                    이것저것 시도해봐도 변화가 없어 답답하시죠?
                  </p>
                </div>

                <div className="w-72 bg-white rounded-lg shadow-lg p-5 border border-gray-100">
                  <div className="flex items-start mb-3">
                    <div className="text-blue-500 mr-3 flex-shrink-0">
                      <svg
                        className="w-6 h-6"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                      </svg>
                    </div>
                    <h3 className="text-base font-bold text-gray-800">
                      경쟁사 격차
                    </h3>
                  </div>

                  {/* 고객 인용구 */}
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-3 rounded">
                    <p className="text-sm text-gray-800 font-medium italic">
                      "경쟁사는 5%인데 우리는 왜 2%일까?"
                    </p>
                  </div>

                  {/* 설명 */}
                  <p className="text-sm text-gray-600">
                    경쟁사는 이미 수십 번의 테스트로 최적화했습니다.
                  </p>
                </div>

                <div className="w-72 bg-white rounded-lg shadow-lg p-5 border border-gray-100">
                  <div className="flex items-start mb-3">
                    <div className="text-green-500 mr-3 flex-shrink-0">
                      <svg
                        className="w-6 h-6"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <h3 className="text-base font-bold text-gray-800">
                      놓친 기회
                    </h3>
                  </div>

                  {/* 고객 인용구 */}
                  <div className="bg-green-50 border-l-4 border-green-400 p-3 mb-3 rounded">
                    <p className="text-sm text-gray-800 font-medium italic">
                      "0.1%만 올려도 월 1천만원 증가인데..."
                    </p>
                  </div>

                  {/* 설명 */}
                  <p className="text-sm text-gray-600">
                    작은 개선이 큰 성과로 이어집니다.
                  </p>
                </div>
              </div>
            </div>
            {/* 스크롤 인디케이터 */}
            <div className="flex justify-center mt-3 gap-1">
              <div className="w-2 h-2 rounded-full bg-blue-600"></div>
              <div className="w-2 h-2 rounded-full bg-gray-300"></div>
              <div className="w-2 h-2 rounded-full bg-gray-300"></div>
            </div>
          </div>

          {/* 데스크탑 버전 - 기존 유지 */}
          <div className="hidden sm:block bg-gray-50 rounded-xl p-8 mb-8">
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="text-orange-500 mr-4 mt-1">
                  <svg
                    className="w-6 h-6"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-1">
                    "전환율 2.5%에서 멈춰있는데, 뭘 바꿔야 할지 모르겠어요"
                  </h3>
                  <p className="text-gray-600">
                    이것저것 시도해봐도 변화가 없어 답답하시죠? 데이터 없이는
                    정답을 찾기 어렵습니다.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="text-orange-500 mr-4 mt-1">
                  <svg
                    className="w-6 h-6"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-1">
                    "경쟁사는 5%인데 우리는 왜 2%일까? 차이가 뭘까?"
                  </h3>
                  <p className="text-gray-600">
                    경쟁사는 이미 수십 번의 테스트로 최적의 조합을 찾았기
                    때문입니다.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="text-orange-500 mr-4 mt-1">
                  <svg
                    className="w-6 h-6"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-1">
                    "전환율 0.1%만 올려도 월 매출 1천만원 증가인데, 방법을
                    모르겠어요"
                  </h3>
                  <p className="text-gray-600">
                    작은 개선이 큰 성과로 이어집니다. A/B 테스트로 그 0.1%를
                    찾아보세요.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 계산기 - 모바일 인터랙티브 버전 */}
          <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-lg p-4 sm:p-6 text-center">
            <p className="text-sm text-gray-600 mb-3">
              💡 A/B 테스트로 달성 가능한 성과
            </p>

            {/* 모바일: 간소화된 버전 */}
            <div className="sm:hidden">
              <div className="bg-white rounded-lg p-4 mb-3">
                <p className="text-sm mb-2">
                  <span className="font-bold">월 방문자 10,000명</span> 기준
                </p>
                <p className="text-sm mb-2">
                  <span className="font-bold">전환율 1% 상승</span> 시
                </p>
                <div className="text-2xl font-bold text-blue-600">
                  월 100건 추가 판매
                </div>
              </div>
              <div className="bg-blue-600 text-white rounded-lg p-3">
                <p className="text-lg font-bold">연간 +6,000만원</p>
                <p className="text-xs opacity-90 mt-1">추가 매출 예상</p>
              </div>
            </div>

            {/* 데스크탑: 기존 버전 */}
            <div className="hidden sm:block">
              <p className="text-lg mb-3">
                <span className="font-bold">월 방문자 10,000명</span> ×
                <span className="font-bold"> 전환율 1% 상승</span> =
                <span className="text-blue-600 font-bold">
                  {" "}
                  월 100건 추가 판매
                </span>
              </p>
              <p className="text-2xl font-bold text-blue-600">
                연간 1,200건 = 약 6,000만원 추가 매출 가능
              </p>
            </div>

            <p className="text-xs sm:text-sm text-gray-600 mt-2">
              * 객단가 5만원 기준 | 실제 고객 평균 상승률 38%
            </p>
          </div>
        </div>
      </section>

      <section className="py-8 sm:py-12 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-4 sm:mb-6">
            <div className="inline-block bg-green-100 text-green-700 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              ✓ 해결책
            </div>
            <h2 className="text-2xl sm:text-4xl font-bold mb-2 sm:mb-4">
              <span className="text-blue-600">2가지 A/B 테스트 방법</span>으로
              <br className="sm:hidden" />
              <span className="hidden sm:inline">
                {" "}
                전환율을 확실하게 높이세요
              </span>
              <span className="sm:hidden"> 전환율 높이기</span>
            </h2>
          </div>

          {/* 카드 - 모든 디바이스 */}
          <div className="max-w-xl mx-auto">
            <div className="flex gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="flex-1 bg-white rounded-lg p-3 sm:p-4 shadow-sm sm:shadow border border-gray-200 hover:shadow-md transition-shadow cursor-pointer">
                <div className="text-xl sm:text-2xl mb-1 sm:mb-2 text-center">
                  🔗
                </div>
                <p className="text-xs sm:text-sm font-bold text-center">
                  URL 분리
                </p>
                <p className="text-xs sm:text-xs text-gray-500 text-center">
                  페이지 전체 비교
                </p>
              </div>
              <div className="flex-1 bg-white rounded-lg p-3 sm:p-4 shadow-sm sm:shadow border border-gray-200 hover:shadow-md transition-shadow cursor-pointer">
                <div className="text-xl sm:text-2xl mb-1 sm:mb-2 text-center">
                  🎨
                </div>
                <p className="text-xs sm:text-sm font-bold text-center">
                  비주얼 에디터
                </p>
                <p className="text-xs sm:text-xs text-gray-500 text-center">
                  클릭으로 수정
                </p>
              </div>
            </div>
            <p className="text-sm sm:text-base text-center text-gray-600 font-medium">
              <span className="sm:hidden">⚡ 개발자 없이 3분 설정</span>
              <span className="hidden sm:inline">
                개발자 없이도 3분이면 충분합니다
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* URL 분리 테스트 섹션 - 개선된 버전 */}
      <section className="py-20 bg-gray-50" id="features">
        <div className="max-w-6xl mx-auto px-8">
          {/* 헤더 */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-block bg-blue-100 text-blue-700 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              방법 1
            </div>

            {/* 모바일 전용 아이콘 */}
            <div className="sm:hidden mb-3">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full">
                <span className="text-2xl">🔗</span>
              </div>
            </div>

            <h2 className="text-2xl sm:text-4xl font-bold mb-3 sm:mb-4">
              <span className="text-blue-600">URL 분리</span>
              <span className="block sm:inline text-xl sm:text-4xl mt-1 sm:mt-0">
                A/B 테스트
              </span>
            </h2>

            {/* 모바일: A vs B 시각화 */}
            <div className="sm:hidden text-base text-gray-600 mb-2">
              <div className="flex items-center justify-center gap-2">
                <span className="bg-gray-100 px-2 py-1 rounded text-xs font-medium">
                  A 페이지
                </span>
                <span className="text-blue-600 font-bold">VS</span>
                <span className="bg-blue-100 px-2 py-1 rounded text-xs font-medium">
                  B 페이지
                </span>
              </div>
              <p className="mt-2 text-sm">전체 페이지 성과 비교</p>
            </div>

            {/* 데스크톱: 기존 설명 */}
            <p className="hidden sm:block text-xl text-gray-600">
              페이지 전체를 비교하는 가장 확실한 테스트 방법
            </p>
          </div>

          {/* 핵심 설명 - 한 눈에 */}
          <div className="bg-white rounded-xl p-4 sm:p-8 shadow-lg mb-12">
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8 items-center">
              {/* 왼쪽: 시각적 도식 - 모바일 최적화 */}
              <div className="text-center">
                {/* 모바일: 세로 배치 */}
                <div className="sm:hidden space-y-4">
                  {/* A안 */}
                  <div className="bg-white rounded-lg shadow-md border border-gray-200 p-2">
                    <div className="bg-gray-100 rounded-t px-2 py-1 border-b">
                      <div className="flex items-center gap-2 justify-center">
                        <div className="flex gap-0.5">
                          <div className="w-1.5 h-1.5 bg-red-400 rounded-full"></div>
                          <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                          <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                        </div>
                        <div className="text-xs text-gray-600 font-mono">
                          mysite.com
                        </div>
                      </div>
                    </div>
                    <div className="p-3">
                      <div className="text-xs text-gray-600 mb-2">
                        A안 (현재)
                      </div>
                      <button className="bg-blue-500 text-white px-3 py-1 rounded text-xs">
                        구매하기
                      </button>
                    </div>
                  </div>

                  {/* VS 표시 */}
                  <div className="flex items-center justify-center">
                    <div className="bg-gray-100 rounded-full px-4 py-2">
                      <span className="text-sm font-bold text-gray-600">
                        VS
                      </span>
                    </div>
                  </div>

                  {/* B안 */}
                  <div className="bg-white rounded-lg shadow-md border-2 border-blue-400 p-2">
                    <div className="bg-gray-100 rounded-t px-2 py-1 border-b">
                      <div className="flex items-center gap-2 justify-center">
                        <div className="flex gap-0.5">
                          <div className="w-1.5 h-1.5 bg-red-400 rounded-full"></div>
                          <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                          <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                        </div>
                        <div className="text-xs text-gray-600 font-mono">
                          mysite.com/test
                        </div>
                      </div>
                    </div>
                    <div className="p-3 bg-blue-50">
                      <div className="text-xs text-blue-600 mb-2">
                        B안 (테스트)
                      </div>
                      <button className="bg-green-500 text-white px-4 py-1.5 rounded text-xs font-bold shadow">
                        지금 구매 →
                      </button>
                    </div>
                  </div>

                  {/* 트래픽 분배 */}
                  <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-xs text-gray-600">방문자</span>
                      <div className="flex gap-0.5">
                        <div className="w-16 h-2 bg-blue-400 rounded-l"></div>
                        <div className="w-16 h-2 bg-green-400 rounded-r"></div>
                      </div>
                      <span className="text-xs font-bold text-gray-700">
                        50:50
                      </span>
                    </div>
                  </div>
                </div>

                {/* 데스크탑: 가로 배치 (기존 유지) */}
                <div className="hidden sm:block space-y-4">
                  {/* 브라우저 창 스타일 */}
                  <div className="flex items-center gap-4">
                    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-2">
                      <div className="bg-gray-100 rounded-t-lg px-3 py-1 border-b">
                        <div className="flex items-center gap-2">
                          <div className="flex gap-1">
                            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                            <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                          </div>
                          <div className="text-xs text-gray-600 font-mono">
                            mysite.com
                          </div>
                        </div>
                      </div>
                      <div className="p-4 bg-gray-50">
                        <div className="text-sm text-gray-600 mb-2">
                          현재 페이지
                        </div>
                        <button className="bg-blue-500 text-white px-4 py-1 rounded text-xs">
                          구매하기
                        </button>
                      </div>
                    </div>

                    <div className="text-2xl text-gray-400">VS</div>

                    <div className="bg-white rounded-lg shadow-md border-2 border-blue-400 p-2">
                      <div className="bg-gray-100 rounded-t-lg px-3 py-1 border-b">
                        <div className="flex items-center gap-2">
                          <div className="flex gap-1">
                            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                            <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                          </div>
                          <div className="text-xs text-gray-600 font-mono">
                            mysite.com/test
                          </div>
                        </div>
                      </div>
                      <div className="p-4 bg-blue-50">
                        <div className="text-sm text-blue-600 mb-2">
                          테스트 페이지
                        </div>
                        <button className="bg-green-500 text-white px-4 py-2 rounded text-xs font-bold shadow">
                          지금 구매 →
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* 트래픽 분배 시각화 */}
                  <div className="bg-gray-100 rounded-lg p-3">
                    <div className="flex items-center justify-center gap-2">
                      <div className="text-sm text-gray-600">방문자</div>
                      <div className="flex gap-1">
                        <div className="w-20 h-2 bg-blue-400 rounded-l"></div>
                        <div className="w-20 h-2 bg-green-400 rounded-r"></div>
                      </div>
                      <div className="text-xs text-gray-500">50:50</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 오른쪽: 핵심 가치 - 모바일에서도 읽기 쉽게 */}
              <div className="mt-6 sm:mt-0">
                <h3 className="text-xl sm:text-2xl font-bold mb-4">
                  언제 사용하나요?
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-blue-600 font-bold text-sm">1</span>
                    </div>
                    <div className="text-sm sm:text-base">
                      <strong>전체 리디자인</strong>을 테스트하고 싶을 때
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-blue-600 font-bold text-sm">2</span>
                    </div>
                    <div className="text-sm sm:text-base">
                      <strong>대규모 변경</strong>으로 위험 부담이 클 때
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-blue-600 font-bold text-sm">3</span>
                    </div>
                    <div className="text-sm sm:text-base">
                      <strong>여러 요소를 한번에</strong> 테스트하고 싶을 때
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* 실제 3단계 프로세스 - URL 분리 테스트용 */}
          <div className="mb-12">
            <h3 className="text-xl font-bold mb-6 text-center">
              실제로 어떻게 사용하나요?
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-gradient-to-br from-blue-100 to-blue-50 rounded-lg p-6 mb-3 border-2 border-blue-200 hover:shadow-lg transition-shadow">
                  <div className="text-4xl mb-3">🔗</div>
                  <div className="inline-block bg-blue-600 text-white text-xs px-3 py-1 rounded-full font-bold mb-3">
                    STEP 1
                  </div>
                  <h4 className="font-bold text-lg mb-2">두 URL 입력</h4>
                  <p className="text-sm text-gray-600">
                    원본 페이지와
                    <br />
                    테스트 페이지 URL 입력
                  </p>
                </div>
              </div>

              <div className="text-center">
                <div className="bg-gradient-to-br from-indigo-100 to-indigo-50 rounded-lg p-6 mb-3 border-2 border-indigo-200 hover:shadow-lg transition-shadow">
                  <div className="text-4xl mb-3">🎯</div>
                  <div className="inline-block bg-indigo-600 text-white text-xs px-3 py-1 rounded-full font-bold mb-3">
                    STEP 2
                  </div>
                  <h4 className="font-bold text-lg mb-2">목표 설정</h4>
                  <p className="text-sm text-gray-600">
                    측정할 전환 목표 지정
                    <br />
                    (구매, 가입 등)
                  </p>
                </div>
              </div>

              <div className="text-center">
                <div className="bg-gradient-to-br from-green-100 to-green-50 rounded-lg p-6 mb-3 border-2 border-green-200 hover:shadow-lg transition-shadow">
                  <div className="text-4xl mb-3">🚀</div>
                  <div className="inline-block bg-green-600 text-white text-xs px-3 py-1 rounded-full font-bold mb-3">
                    STEP 3
                  </div>
                  <h4 className="font-bold text-lg mb-2">테스트 시작</h4>
                  <p className="text-sm text-gray-600">
                    시작 버튼 클릭하면
                    <br />
                    자동으로 트래픽 분배
                  </p>
                </div>
              </div>
            </div>

            {/* 소요 시간 강조 */}
            <div className="mt-6 text-center">
              <div className="inline-flex items-center bg-blue-50 px-6 py-3 rounded-full border border-blue-200">
                <span className="text-2xl mr-2">⏱️</span>
                <span className="text-blue-900 font-bold">
                  전체 소요 시간: 단 3분
                </span>
              </div>
            </div>
          </div>

          {/* 실제 활용 예시 - 색상 차별화 */}
          <div className="mb-12">
            <h3 className="text-xl font-bold mb-6 text-center">
              실제 성공 사례
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-white p-6 rounded-lg border border-blue-200 hover:shadow-lg transition-shadow">
                <div className="text-3xl mb-3">🛍️</div>
                <h4 className="font-bold mb-2">랜딩 페이지 개선</h4>
                <p className="text-sm text-gray-600 mb-3">
                  광고 랜딩 페이지 전체 리디자인
                </p>
                <div className="text-blue-600 font-bold text-lg">
                  → 전환율 +28%
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-white p-6 rounded-lg border border-green-200 hover:shadow-lg transition-shadow">
                <div className="text-3xl mb-3">💳</div>
                <h4 className="font-bold mb-2">결제 프로세스</h4>
                <p className="text-sm text-gray-600 mb-3">
                  체크아웃 과정 전면 개편
                </p>
                <div className="text-green-600 font-bold text-lg">
                  → 구매완료 +19%
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-lg border border-purple-200 hover:shadow-lg transition-shadow">
                <div className="text-3xl mb-3">✍️</div>
                <h4 className="font-bold mb-2">회원가입 개선</h4>
                <p className="text-sm text-gray-600 mb-3">
                  가입 절차 간소화 테스트
                </p>
                <div className="text-purple-600 font-bold text-lg">
                  → 가입률 +35%
                </div>
              </div>
            </div>
          </div>

          {/* 설정 시간 강조 - 더 눈에 띄게 */}
          <div className="bg-blue-600 text-white p-6 rounded-lg shadow-xl text-center">
            <div className="inline-flex items-center">
              <span className="text-3xl mr-3">⏱️</span>
              <div className="text-left">
                <span className="text-xl sm:text-2xl font-bold block">
                  단 3분이면 설정 완료
                </span>
                <span className="text-xs sm:text-sm opacity-90">
                  원본 URL과 테스트 URL만<br className="sm:hidden" /> 입력하면 바로 시작
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 비주얼 에디터 섹션 - 개선된 버전 */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-8 sm:mb-12">
            {/* 배지 */}
            <div className="inline-block bg-purple-100 text-purple-700 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              방법 2
            </div>

            {/* 모바일 전용 아이콘 */}
            <div className="sm:hidden mb-3">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full">
                <span className="text-2xl">🎨</span>
              </div>
            </div>

            {/* 제목 */}
            <h2 className="text-2xl sm:text-4xl font-bold mb-3 sm:mb-4">
              <span className="text-purple-600">비주얼 에디터</span>
              <span className="block sm:inline text-xl sm:text-4xl mt-1 sm:mt-0">
                A/B 테스트
              </span>
            </h2>

            {/* 설명 - 모바일에서 장점 강조 */}
            <div className="text-base sm:text-xl text-gray-600">
              <p className="sm:hidden">
                <span className="font-bold text-purple-600">🖱️ 클릭</span>으로
                수정
                <br />
                <span className="font-bold text-purple-600">👨‍💻 코딩</span>{" "}
                불필요
              </p>
              <p className="hidden sm:block">
                클릭만으로 웹사이트 요소를 변경하고 테스트
              </p>
            </div>
          </div>

          {/* 핵심 설명 - 개선된 시각적 표현 */}
          <div className="bg-white rounded-xl p-8 shadow-lg mb-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* 왼쪽: 더 직관적인 시각적 예시 */}
              <div className="text-center">
                <div className="bg-gradient-to-br from-purple-50 to-white rounded-lg p-6 border-2 border-purple-300 shadow-md relative">
                  {/* 상단에 "간단한 3단계" 배지 추가 */}
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-xs px-4 py-1 rounded-full font-bold whitespace-nowrap">
                    클릭 3번으로 완성
                  </div>

                  {/* 에디터 툴바 모형 - 아이콘 개선 */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2 mb-4">
                    <div className="flex gap-2 items-center justify-center">
                      <div className="w-6 h-6 bg-gray-200 rounded cursor-pointer hover:bg-gray-300 transition"></div>
                      <div className="w-6 h-6 bg-blue-500 rounded cursor-pointer hover:bg-blue-600 transition"></div>
                      <div className="w-6 h-6 bg-green-500 rounded cursor-pointer hover:bg-green-600 transition"></div>
                      <div className="w-6 h-6 bg-red-500 rounded cursor-pointer hover:bg-red-600 transition ring-2 ring-purple-400"></div>
                      <div className="text-xs text-gray-500 ml-2">
                        ← 색상 선택
                      </div>
                    </div>
                  </div>

                  {/* 버튼 변경 애니메이션 효과 */}
                  <div className="relative mb-4">
                    <button className="bg-blue-500 text-white px-6 py-2 rounded opacity-40 line-through">
                      구매하기
                    </button>
                    <div className="absolute -top-2 -right-2 bg-purple-500 text-white text-xs px-2 py-1 rounded-full animate-bounce shadow-lg">
                      1️⃣ 클릭!
                    </div>
                  </div>

                  <div className="my-4">
                    <svg
                      className="w-8 h-8 text-purple-500 mx-auto animate-pulse"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-11a1 1 0 112 0v3.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L9 10.586V7z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>

                  <button className="bg-green-500 text-white px-8 py-3 rounded-lg font-bold shadow-lg transform hover:scale-105 transition-transform relative">
                    지금 구매하기 →{/* 완성 표시 */}
                    <div className="absolute -top-2 -right-2 bg-green-600 text-white text-xs px-2 py-1 rounded-full">
                      ✓ 완성
                    </div>
                  </button>
                </div>

                {/* 하단 메시지 강화 */}
                <div className="mt-4 space-y-1">
                  <p className="text-sm text-gray-600 font-medium">
                    🎯 마우스로 선택 → 수정 → 저장. 끝!
                  </p>
                  <p className="text-xs text-purple-600 font-bold">
                    개발 지식 0% 필요
                  </p>
                </div>
              </div>

              {/* 오른쪽: 핵심 가치 - 아이콘 개선 */}
              {/* 오른쪽: 핵심 가치 - 아이콘 개선 */}
              <div className="mt-6 sm:mt-0">
                <h3 className="text-xl sm:text-2xl font-bold mb-4">
                  언제 사용하나요?
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-purple-600 font-bold text-sm">
                        1
                      </span>
                    </div>
                    <div className="text-sm sm:text-base">
                      <strong>버튼, 텍스트, 이미지</strong> 등 개별 요소 테스트
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-purple-600 font-bold text-sm">
                        2
                      </span>
                    </div>
                    <div className="text-sm sm:text-base">
                      <strong>개발팀 없이</strong> 마케터가 직접 테스트
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-purple-600 font-bold text-sm">
                        3
                      </span>
                    </div>
                    <div className="text-sm sm:text-base">
                      <strong>빠른 실험</strong>이 필요할 때 (매일 새로운
                      테스트)
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* 실제 3단계 프로세스 - GIF 버전 */}
          <div className="mb-12">
            <h3 className="text-xl font-bold mb-6 text-center">
              실제로 어떻게 사용하나요?
            </h3>

            {/* Step 1 */}
            <div className="mb-10">
              <div className="flex items-center mb-4">
                <div className="bg-purple-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg mr-4">
                  1
                </div>
                <h4 className="text-xl font-bold text-gray-900">
                  테스트 페이지 URL 입력
                </h4>
              </div>
              <p className="text-gray-600 mb-5 text-base">
                테스트 페이지 URL을 복사해서 붙여넣기만 하세요.
              </p>
              <div className="bg-purple-50 rounded-xl p-5 border-2 border-purple-200">
                <img
                  src="https://cdn.switchuplab.com/marketing/email/2025-ebook-campaign/step1-url.gif"
                  alt="1단계: URL 입력"
                  className="w-full max-w-2xl mx-auto rounded-lg shadow-lg border-2 border-gray-300"
                />
              </div>
            </div>

            {/* Step 2 */}
            <div className="mb-10">
              <div className="flex items-center mb-4">
                <div className="bg-orange-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg mr-4">
                  2
                </div>
                <h4 className="text-xl font-bold text-gray-900">
                  시각적 편집기로 B안 만들기
                </h4>
              </div>
              <p className="text-gray-600 mb-5 text-base">
                코드를 몰라도 괜찮습니다. 마우스 클릭만으로 텍스트, 색상,
                이미지를 바꾸고 바로 B안을 만드세요.
              </p>
              <div className="bg-orange-50 rounded-xl p-5 border-2 border-orange-200">
                <img
                  src="https://cdn.switchuplab.com/marketing/email/2025-ebook-campaign/step2-edit.gif"
                  alt="2단계: 시각적 편집"
                  className="w-full max-w-2xl mx-auto rounded-lg shadow-lg border-2 border-orange-300"
                />
              </div>
            </div>

            {/* Step 3 */}
            <div className="mb-10">
              <div className="flex items-center mb-4">
                <div className="bg-green-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg mr-4">
                  3
                </div>
                <h4 className="text-xl font-bold text-gray-900">
                  목표 설정 후 테스트 시작!
                </h4>
              </div>
              <p className="text-gray-600 mb-5 text-base">
                전환율로 측정할 목표(예: 버튼 클릭, 회원가입)를 설정하고 시작
                버튼을 누르면 모든 준비가 끝납니다.
              </p>
              <div className="bg-green-50 rounded-xl p-5 border-2 border-green-200">
                <img
                  src="https://cdn.switchuplab.com/marketing/email/2025-ebook-campaign/step3-start.gif"
                  alt="3단계: 테스트 시작"
                  className="w-full max-w-2xl mx-auto rounded-lg shadow-lg border-2 border-green-300"
                />
              </div>
            </div>

            {/* 소요 시간 강조 */}
            <div className="text-center">
              <div className="inline-flex items-center bg-purple-50 px-6 py-3 rounded-full border border-purple-200">
                <span className="text-2xl mr-2">⏱️</span>
                <span className="text-purple-900 font-bold">
                  전체 소요 시간: 단 5분
                </span>
              </div>
            </div>
          </div>

          {/* 테스트 가능한 요소들 - 더 시각적으로 개선 */}
          <div className="mb-12">
            <h3 className="text-xl font-bold mb-6 text-center">
              무엇을 테스트할 수 있나요?
            </h3>
            <div className="grid grid-cols-4 gap-4 max-w-3xl mx-auto">
              <div className="text-center group hover:scale-105 transition-transform">
                <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg p-5 mb-2 shadow-md">
                  <span className="text-3xl">🎨</span>
                </div>
                <p className="text-sm font-medium">색상</p>
              </div>
              <div className="text-center group hover:scale-105 transition-transform">
                <div className="bg-gradient-to-br from-purple-100 to-blue-100 rounded-lg p-5 mb-2 shadow-md">
                  <span className="text-3xl">✏️</span>
                </div>
                <p className="text-sm font-medium">텍스트</p>
              </div>
              <div className="text-center group hover:scale-105 transition-transform">
                <div className="bg-gradient-to-br from-purple-100 to-green-100 rounded-lg p-5 mb-2 shadow-md">
                  <span className="text-3xl">📷</span>
                </div>
                <p className="text-sm font-medium">이미지</p>
              </div>
              <div className="text-center group hover:scale-105 transition-transform">
                <div className="bg-gradient-to-br from-purple-100 to-orange-100 rounded-lg p-5 mb-2 shadow-md">
                  <span className="text-3xl">📐</span>
                </div>
                <p className="text-sm font-medium">레이아웃</p>
              </div>
            </div>
          </div>

          {/* 실제 성과 사례 - 색상 차별화 */}
          <div className="mb-12">
            <h3 className="text-xl font-bold mb-6 text-center">
              실제 성공 사례
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-orange-50 to-white p-6 rounded-lg border border-orange-200 hover:shadow-lg transition-shadow">
                <div className="text-3xl mb-3">🔘</div>
                <h4 className="font-bold mb-2">CTA 버튼 색상</h4>
                <p className="text-sm text-gray-600 mb-3">
                  파란색 → 초록색 변경
                </p>
                <div className="text-orange-600 font-bold text-lg">
                  → 클릭률 +18%
                </div>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-white p-6 rounded-lg border border-pink-200 hover:shadow-lg transition-shadow">
                <div className="text-3xl mb-3">📝</div>
                <h4 className="font-bold mb-2">헤드라인 문구</h4>
                <p className="text-sm text-gray-600 mb-3">
                  혜택 중심 메시지로 변경
                </p>
                <div className="text-pink-600 font-bold text-lg">
                  → 전환율 +24%
                </div>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-white p-6 rounded-lg border border-indigo-200 hover:shadow-lg transition-shadow">
                <div className="text-3xl mb-3">📍</div>
                <h4 className="font-bold mb-2">버튼 위치</h4>
                <p className="text-sm text-gray-600 mb-3">
                  스크롤 없이 보이게 이동
                </p>
                <div className="text-indigo-600 font-bold text-lg">
                  → 신청률 +31%
                </div>
              </div>
            </div>
          </div>

          {/* 설정 시간 강조 - 더 눈에 띄게 */}
          <div className="bg-purple-600 text-white p-6 rounded-lg shadow-xl text-center">
            <div className="inline-flex items-center">
              <span className="text-3xl mr-3">🚀</span>
              <div className="text-left">
                <span className="text-xl sm:text-2xl font-bold block">
                  마우스 클릭만으로 완성
                </span>
                <span className="text-xs sm:text-sm opacity-90">
                  드래그 & 드롭으로 요소 수정, 바로 테스트 시작
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* A/B 테스트 컨설팅 섹션 */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 lg:p-8 border border-blue-200">
            <div className="text-center mb-8">
              <span className="text-3xl sm:text-4xl mb-3 block">🎯</span>
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-3">
                A/B 테스트, 혼자 고민하지 마세요
              </h3>
              <p className="text-sm sm:text-base text-gray-600">
                전문 컨설턴트가 처음부터 끝까지 함께합니다
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
              {/* 왼쪽: 고민 */}
              <div className="bg-white rounded-xl p-5 lg:p-6 shadow-sm">
                <h4 className="font-bold text-gray-900 mb-5 text-base sm:text-lg flex items-center">
                  <span className="text-red-500 mr-2">💭</span>
                  이런 고민 있으신가요?
                </h4>
                <div className="space-y-3">
                  {[
                    "무엇을 테스트해야 할지 모르겠어요",
                    "결과 해석이 어려워요",
                    "통계적 유의성 판단이 어려워요",
                    "개선 아이디어가 떠오르지 않아요",
                  ].map((problem, i) => (
                    <div key={i} className="flex items-start space-x-3">
                      <span className="text-red-500 flex-shrink-0 text-lg">
                        ❌
                      </span>
                      <span className="text-gray-700 text-sm sm:text-base pt-0.5">
                        {problem}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 오른쪽: 해결책 */}
              <div className="bg-white rounded-xl p-5 lg:p-6 shadow-sm">
                <h4 className="font-bold text-gray-900 mb-5 text-base sm:text-lg flex items-center">
                  <span className="text-blue-600 mr-2">✨</span>
                  전문가가 해결해드려요
                </h4>
                <div className="space-y-3">
                  {[
                    { icon: "📋", text: "비즈니스 목표 기반 테스트 설계" },
                    { icon: "📊", text: "실시간 데이터 분석 및 인사이트" },
                    { icon: "🎓", text: "통계 이론과 실전 노하우 전수" },
                    { icon: "💡", text: "업계 베스트 프랙티스 공유" },
                  ].map((solution, i) => (
                    <div key={i} className="flex items-start space-x-3">
                      <span className="flex-shrink-0 text-lg">
                        {solution.icon}
                      </span>
                      <span className="text-gray-700 text-sm sm:text-base font-medium pt-0.5">
                        {solution.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 하단 강조 메시지 */}
            <div className="mt-6 lg:mt-8 text-center">
              <div className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-full text-xs sm:text-sm font-medium">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                30일 무료 체험 기간 동안 모든 컨설팅 무료 제공
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              <span className="text-blue-600">실제 기업</span>의 A/B 테스트 성과
            </h2>
            <p className="text-lg lg:text-xl text-gray-600">
              2025년 검증된 전환율 개선 사례
            </p>
          </div>

          {/* 업종별 성과 카드 - 1열 레이아웃 */}
          <div className="space-y-6 mb-12">
            {/* 코딩 교육 기업 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="flex flex-col lg:flex-row">
                {/* 메인 콘텐츠 영역 */}
                <div className="flex-1 p-6 lg:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-6">
                    <div>
                      <h3 className="text-xl lg:text-2xl font-bold mb-2">
                        💻 코딩 교육 기업 N사
                      </h3>
                      <p className="text-sm lg:text-base text-gray-600">
                        임직원 100명 | 연매출 100억 | 월 방문자 12만명
                      </p>
                    </div>
                    <span className="mt-3 sm:mt-0 inline-block bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs lg:text-sm font-bold">
                      2025년 1분기
                    </span>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-semibold text-gray-800">
                          웨비나 신청 버튼 최적화
                        </h4>
                        <span className="text-green-600 font-bold text-lg">
                          +65%
                        </span>
                      </div>
                      <div className="space-y-2 text-sm lg:text-base">
                        <div className="flex gap-2">
                          <span className="text-gray-500 font-medium">
                            A안 (원본):
                          </span>
                          <span className="text-gray-700">
                            페이지 중간에만 버튼 배치
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-gray-500 font-medium">
                            B안 (개선):
                          </span>
                          <span className="text-gray-700">
                            우측 하단 플로팅 버튼 추가
                          </span>
                        </div>
                        <div className="pt-2 mt-2 border-t border-gray-200">
                          <span className="text-blue-600 font-bold">
                            신청률: 8.2% → 13.5%
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-semibold text-gray-800">
                          결제 페이지 혜택 문구
                        </h4>
                        <span className="text-green-600 font-bold text-lg">
                          +41%
                        </span>
                      </div>
                      <div className="space-y-2 text-sm lg:text-base">
                        <div className="flex gap-2">
                          <span className="text-gray-500 font-medium">
                            A안 (원본):
                          </span>
                          <span className="text-gray-700">
                            결제 정보만 표시
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-gray-500 font-medium">
                            B안 (개선):
                          </span>
                          <span className="text-gray-700">
                            "실무 프로젝트 세미나 무료" 추가
                          </span>
                        </div>
                        <div className="pt-2 mt-2 border-t border-gray-200">
                          <span className="text-blue-600 font-bold">
                            결제율: 13.67% → 19.29%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 성과 요약 패널 */}
                <div className="w-full lg:w-72 bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 lg:p-8">
                  <div className="flex flex-row lg:flex-col items-center justify-around lg:justify-center lg:text-center h-full">
                    <div className="text-center lg:mb-6">
                      <p className="text-4xl lg:text-5xl font-bold mb-2">
                        +53%
                      </p>
                      <p className="text-sm lg:text-base text-blue-100">
                        평균 상승률
                      </p>
                    </div>

                    <div className="border-l lg:border-l-0 lg:border-t border-blue-400 pl-6 lg:pl-0 lg:pt-6 lg:w-full">
                      <p className="text-sm text-blue-100 mb-1">월 추가 매출</p>
                      <p className="text-2xl lg:text-3xl font-bold">
                        3,500만원
                      </p>
                    </div>

                    <div className="hidden lg:block mt-6 text-sm text-blue-100">
                      <p>테스트 기간: 3주</p>
                      <p>참여자: 21,000명</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 페미닌 케어 브랜드 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="flex flex-col lg:flex-row">
                <div className="flex-1 p-6 lg:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-6">
                    <div>
                      <h3 className="text-xl lg:text-2xl font-bold mb-2">
                        🌸 페미닌 케어 브랜드 C사
                      </h3>
                      <p className="text-sm lg:text-base text-gray-600">
                        임직원 200명 | 연매출 1500억 | 월 방문자 30만명
                      </p>
                    </div>
                    <span className="mt-3 sm:mt-0 inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs lg:text-sm font-bold">
                      2025년 1분기
                    </span>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-semibold text-gray-800">
                          상세페이지 메시지 전략
                        </h4>
                        <span className="text-green-600 font-bold text-lg">
                          +60%
                        </span>
                      </div>
                      <div className="space-y-2 text-sm lg:text-base">
                        <div className="flex gap-2">
                          <span className="text-gray-500 font-medium">
                            A안 (원본):
                          </span>
                          <span className="text-gray-700">
                            "특허받은 7가지 성분" 기능 강조
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-gray-500 font-medium">
                            B안 (개선):
                          </span>
                          <span className="text-gray-700">
                            "선착순 100명 한정 특가" 혜택
                          </span>
                        </div>
                        <div className="pt-2 mt-2 border-t border-gray-200">
                          <span className="text-blue-600 font-bold">
                            전환율: 1.5% → 2.4%
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-semibold text-gray-800">
                          신뢰도 요소 테스트
                        </h4>
                        <span className="text-green-600 font-bold text-lg">
                          +21%
                        </span>
                      </div>
                      <div className="space-y-2 text-sm lg:text-base">
                        <div className="flex gap-2">
                          <span className="text-gray-500 font-medium">
                            A안 (원본):
                          </span>
                          <span className="text-gray-700">
                            임상시험 결과 강조
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-gray-500 font-medium">
                            B안 (개선):
                          </span>
                          <span className="text-gray-700">
                            약국/산후조리원 판매량 강조
                          </span>
                        </div>
                        <div className="pt-2 mt-2 border-t border-gray-200">
                          <span className="text-blue-600 font-bold">
                            결제율: 3.4% → 4.1%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="w-full lg:w-72 bg-gradient-to-br from-green-500 to-green-600 text-white p-6 lg:p-8">
                  <div className="flex flex-row lg:flex-col items-center justify-around lg:justify-center lg:text-center h-full">
                    <div className="text-center lg:mb-6">
                      <p className="text-4xl lg:text-5xl font-bold mb-2">
                        +40%
                      </p>
                      <p className="text-sm lg:text-base text-green-100">
                        평균 상승률
                      </p>
                    </div>

                    <div className="border-l lg:border-l-0 lg:border-t border-green-400 pl-6 lg:pl-0 lg:pt-6 lg:w-full">
                      <p className="text-sm text-green-100 mb-1">
                        월 추가 매출
                      </p>
                      <p className="text-2xl lg:text-3xl font-bold">
                        8,000만원
                      </p>
                    </div>

                    <div className="hidden lg:block mt-6 text-sm text-green-100">
                      <p>테스트 기간: 10일</p>
                      <p>참여자: 32,000명</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 산삼 건강식품 브랜드 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="flex flex-col lg:flex-row">
                <div className="flex-1 p-6 lg:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-6">
                    <div>
                      <h3 className="text-xl lg:text-2xl font-bold mb-2">
                        🌿 산삼 건강식품 브랜드 B사
                      </h3>
                      <p className="text-sm lg:text-base text-gray-600">
                        임직원 30명 | 연매출 200억 | 월 방문자 10만명
                      </p>
                    </div>
                    <span className="mt-3 sm:mt-0 inline-block bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs lg:text-sm font-bold">
                      2025년 2분기
                    </span>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                    <div className="bg-gray-50 rounded-lg p-4 lg:col-span-2">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-semibold text-gray-800">
                          상품 설명 포커스 변경
                        </h4>
                        <span className="text-green-600 font-bold text-lg">
                          +50%
                        </span>
                      </div>
                      <div className="space-y-2 text-sm lg:text-base">
                        <div className="flex gap-2">
                          <span className="text-gray-500 font-medium">
                            A안 (원본):
                          </span>
                          <span className="text-gray-700">
                            체력 강화 효능 중심
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-gray-500 font-medium">
                            B안 (개선):
                          </span>
                          <span className="text-gray-700">
                            6년근 산삼 프리미엄 강조
                          </span>
                        </div>
                        <div className="pt-2 mt-2 border-t border-gray-200">
                          <span className="text-blue-600 font-bold">
                            전환율: 0.8% → 1.2%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="w-full lg:w-72 bg-gradient-to-br from-purple-500 to-purple-600 text-white p-6 lg:p-8">
                  <div className="flex flex-row lg:flex-col items-center justify-around lg:justify-center lg:text-center h-full">
                    <div className="text-center lg:mb-6">
                      <p className="text-4xl lg:text-5xl font-bold mb-2">
                        +50%
                      </p>
                      <p className="text-sm lg:text-base text-purple-100">
                        전환율 상승
                      </p>
                    </div>

                    <div className="border-l lg:border-l-0 lg:border-t border-purple-400 pl-6 lg:pl-0 lg:pt-6 lg:w-full">
                      <p className="text-sm text-purple-100 mb-1">
                        월 추가 매출
                      </p>
                      <p className="text-2xl lg:text-3xl font-bold">
                        6,000만원
                      </p>
                    </div>

                    <div className="hidden lg:block mt-6 text-sm text-purple-100">
                      <p>테스트 기간: 2주</p>
                      <p>참여자: 20,000명</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 건강식품 이커머스 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="flex flex-col lg:flex-row">
                <div className="flex-1 p-6 lg:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-6">
                    <div>
                      <h3 className="text-xl lg:text-2xl font-bold mb-2">
                        🌿 혈당 개선 건강식품 D사
                      </h3>
                      <p className="text-sm lg:text-base text-gray-600">
                        임직원 30명 | 연매출 100억 | 월 방문자 30만명
                      </p>
                    </div>
                    <span className="mt-3 sm:mt-0 inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs lg:text-sm font-bold">
                      2025년 1분기
                    </span>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-semibold text-gray-800">
                          구독 버튼 문구
                        </h4>
                        <span className="text-green-600 font-bold text-lg">
                          +50%
                        </span>
                      </div>
                      <div className="space-y-2 text-sm lg:text-base">
                        <div className="flex gap-2">
                          <span className="text-gray-500 font-medium">
                            A안 (원본):
                          </span>
                          <span className="text-gray-700">"건강 편지"</span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-gray-500 font-medium">
                            B안 (개선):
                          </span>
                          <span className="text-gray-700">
                            "당뇨 치료 가이드북"
                          </span>
                        </div>
                        <div className="pt-2 mt-2 border-t border-gray-200">
                          <span className="text-blue-600 font-bold">
                            클릭률: 8% → 12%
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-semibold text-gray-800">
                          페이지 상단 메시지
                        </h4>
                        <span className="text-green-600 font-bold text-lg">
                          +40%
                        </span>
                      </div>
                      <div className="space-y-2 text-sm lg:text-base">
                        <div className="flex gap-2">
                          <span className="text-gray-500 font-medium">
                            A안 (원본):
                          </span>
                          <span className="text-gray-700">
                            당뇨 연구 시작 이유
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-gray-500 font-medium">
                            B안 (개선):
                          </span>
                          <span className="text-gray-700">
                            50대 타겟 감성 메시지
                          </span>
                        </div>
                        <div className="pt-2 mt-2 border-t border-gray-200">
                          <span className="text-blue-600 font-bold">
                            구독률: 3% → 4.2%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="w-full lg:w-72 bg-gradient-to-br from-green-500 to-green-600 text-white p-6 lg:p-8">
                  <div className="flex flex-row lg:flex-col items-center justify-around lg:justify-center lg:text-center h-full">
                    <div className="text-center lg:mb-6">
                      <p className="text-4xl lg:text-5xl font-bold mb-2">
                        1억원
                      </p>
                      <p className="text-sm lg:text-base text-green-100">
                        월 광고비 절감
                      </p>
                    </div>

                    <div className="border-l lg:border-l-0 lg:border-t border-green-400 pl-6 lg:pl-0 lg:pt-6 lg:w-full">
                      <p className="text-sm text-green-100 mb-1">평균 상승률</p>
                      <p className="text-2xl lg:text-3xl font-bold">+45%</p>
                    </div>

                    <div className="hidden lg:block mt-6 text-sm text-green-100">
                      <p>테스트 기간: 1주</p>
                      <p>참여자: 15,000명</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>


      {/* 히트맵 섹션 - 비주얼 에디터 섹션 다음에 삽입 */}
      <section className="py-12 sm:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          {/* 헤더 */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-block bg-orange-100 text-orange-700 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              🔥 신규 기능
            </div>

            {/* 모바일 전용 아이콘 */}
            <div className="sm:hidden mb-3">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-100 to-red-200 rounded-full">
                <span className="text-2xl">🔥</span>
              </div>
            </div>

            <h2 className="text-2xl sm:text-4xl font-bold mb-3 sm:mb-4">
              <span className="text-orange-600">방문자 행동</span>을
              <br className="sm:hidden" />
              <span className="block sm:inline mt-1 sm:mt-0"> 히트맵 분석으로 한눈에</span>
            </h2>

            <div className="text-base sm:text-xl text-gray-600">
              <p className="sm:hidden">
                클릭, 스크롤, 체류 데이터를
                <br /><span className="font-bold text-orange-600">색상 지도</span>로 보여줍니다
              </p>
              <p className="hidden sm:block">
                클릭, 스크롤, 체류 데이터를 색상 지도로 시각화합니다
              </p>
            </div>
          </div>

          {/* 히트맵 3종 카드 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12">

            {/* 클릭 히트맵 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow">
              {/* CSS 목업 - 클릭 히트맵 */}
              <div className="relative h-48 sm:h-56 bg-gray-100 overflow-hidden">
                <div className="absolute inset-3 bg-white rounded shadow-sm">
                  <div className="h-4 bg-gray-200 rounded-t"></div>
                  <div className="flex gap-2 px-3 pt-2">
                    <div className="w-8 h-2 bg-gray-200 rounded"></div>
                    <div className="w-8 h-2 bg-gray-200 rounded"></div>
                    <div className="w-8 h-2 bg-gray-200 rounded"></div>
                  </div>
                  <div className="px-3 pt-3">
                    <div className="w-24 h-3 bg-gray-300 rounded mb-2"></div>
                    <div className="w-32 h-2 bg-gray-200 rounded mb-3"></div>
                  </div>
                  <div className="px-3">
                    <div className="relative inline-block">
                      <div className="w-20 h-6 bg-blue-500 rounded text-white text-[8px] flex items-center justify-center">구매하기</div>
                      <div className="absolute -inset-3 bg-red-500 opacity-40 rounded-full blur-md"></div>
                      <div className="absolute -inset-1 bg-red-600 opacity-30 rounded-full blur-sm"></div>
                    </div>
                  </div>
                  <div className="absolute top-8 left-5">
                    <div className="w-6 h-6 bg-yellow-400 opacity-30 rounded-full blur-sm"></div>
                  </div>
                  <div className="absolute top-8 left-14">
                    <div className="w-4 h-4 bg-green-400 opacity-25 rounded-full blur-sm"></div>
                  </div>
                  <div className="absolute bottom-8 right-6">
                    <div className="w-8 h-8 bg-orange-400 opacity-30 rounded-full blur-md"></div>
                  </div>
                  <div className="absolute bottom-12 left-8">
                    <div className="w-5 h-5 bg-yellow-300 opacity-20 rounded-full blur-sm"></div>
                  </div>
                </div>
              </div>

              <div className="p-4 sm:p-5">
                <div className="flex items-center mb-2">
                  <span className="text-xl mr-2">🖱️</span>
                  <h3 className="text-lg font-bold">클릭 히트맵</h3>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  방문자가 어디를 가장 많이 클릭하는지 시각적으로 확인
                </p>

              </div>
            </div>

            {/* 스크롤 히트맵 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow">
              <div className="relative h-48 sm:h-56 bg-gray-100 overflow-hidden">
                <div className="absolute inset-3 bg-white rounded shadow-sm overflow-hidden">
                  <div className="h-4 bg-gray-200 rounded-t"></div>
                  <div className="absolute inset-0 top-4" style={{
                    background: 'linear-gradient(to bottom, rgba(239,68,68,0.35) 0%, rgba(249,115,22,0.3) 25%, rgba(234,179,8,0.25) 50%, rgba(34,197,94,0.2) 70%, rgba(59,130,246,0.15) 85%, rgba(99,102,241,0.08) 100%)'
                  }}></div>
                  <div className="relative px-3 pt-3 space-y-2">
                    <div className="w-24 h-3 bg-gray-300 rounded"></div>
                    <div className="w-32 h-2 bg-gray-200 rounded"></div>
                    <div className="w-28 h-2 bg-gray-200 rounded"></div>
                    <div className="w-20 h-6 bg-blue-500 rounded"></div>
                    <div className="w-32 h-2 bg-gray-200 rounded"></div>
                    <div className="w-24 h-2 bg-gray-200 rounded"></div>
                    <div className="w-28 h-3 bg-gray-300 rounded"></div>
                    <div className="w-32 h-2 bg-gray-200 rounded"></div>
                    <div className="w-20 h-2 bg-gray-200 rounded"></div>
                  </div>
                  <div className="absolute right-1 top-6 bg-red-500 text-white text-[7px] px-1 rounded font-bold">100%</div>
                  <div className="absolute right-1 top-[45%] bg-yellow-500 text-white text-[7px] px-1 rounded font-bold">68%</div>
                  <div className="absolute right-1 bottom-4 bg-blue-500 text-white text-[7px] px-1 rounded font-bold">23%</div>
                </div>
              </div>

              <div className="p-4 sm:p-5">
                <div className="flex items-center mb-2">
                  <span className="text-xl mr-2">📜</span>
                  <h3 className="text-lg font-bold">스크롤 히트맵</h3>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  방문자가 페이지를 어디까지 스크롤하는지 한눈에 확인
                </p>
              </div>
            </div>

            {/* 어텐션 히트맵 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow">
              <div className="relative h-48 sm:h-56 bg-gray-100 overflow-hidden">
                <div className="absolute inset-3 bg-white rounded shadow-sm overflow-hidden">
                  <div className="h-4 bg-gray-200 rounded-t"></div>
                  <div className="relative px-3 pt-3 space-y-2">
                    <div className="w-24 h-3 bg-gray-300 rounded"></div>
                    <div className="w-32 h-2 bg-gray-200 rounded"></div>
                    <div className="w-28 h-2 bg-gray-200 rounded"></div>
                    <div className="w-20 h-6 bg-blue-500 rounded"></div>
                    <div className="w-32 h-2 bg-gray-200 rounded"></div>
                    <div className="w-24 h-3 bg-gray-300 rounded"></div>
                    <div className="w-28 h-2 bg-gray-200 rounded"></div>
                    <div className="w-32 h-2 bg-gray-200 rounded"></div>
                    <div className="w-20 h-2 bg-gray-200 rounded"></div>
                  </div>
                  <div className="absolute top-10 left-3 right-3 h-12 bg-red-500 opacity-25 rounded blur-sm"></div>
                  <div className="absolute top-10 left-3 right-3 h-8 bg-red-600 opacity-20 rounded blur-md"></div>
                  <div className="absolute top-[55%] left-3 right-3 h-10 bg-orange-400 opacity-20 rounded blur-sm"></div>
                  <div className="absolute bottom-6 left-3 right-3 h-8 bg-yellow-300 opacity-15 rounded blur-sm"></div>
                  <div className="absolute left-2 top-12 bg-red-500 text-white text-[7px] px-1 rounded font-bold">12초</div>
                  <div className="absolute left-2 top-[57%] bg-orange-500 text-white text-[7px] px-1 rounded font-bold">6초</div>
                  <div className="absolute left-2 bottom-8 bg-yellow-500 text-white text-[7px] px-1 rounded font-bold">2초</div>
                </div>
              </div>

              <div className="p-4 sm:p-5">
                <div className="flex items-center mb-2">
                  <span className="text-xl mr-2">👁️</span>
                  <h3 className="text-lg font-bold">체류시간 히트맵</h3>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  방문자가 어느 영역에서 오래 머무르는지 체류 시간으로 분석
                </p>
              </div>
            </div>
          </div>

          {/* 히트맵 문제/이점 섹션 - 3종 카드 다음에 삽입 */}

          {/* 문제 제기 */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-8 mb-8 sm:mb-12">
            <div className="text-center mb-6 sm:mb-8">
              <h3 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3">
                지금 내 사이트에서 <span className="text-red-500">무슨 일이 벌어지는지</span> <br className="sm:hidden" />알고 있나요?
              </h3>
              <p className="text-sm sm:text-base text-gray-500">
                대부분의 기업은 방문자 행동을 모른 채 사이트를 운영합니다
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
              {/* 히트맵 없이 */}
              <div className="bg-red-50 rounded-xl p-4 sm:p-6 border-l-4 border-red-400">
                <h4 className="font-bold text-base sm:text-lg mb-3 sm:mb-4">
                  😰 히트맵 없이 운영하면
                </h4>
                <div className="space-y-3">
                  <div className="bg-white rounded-lg p-3">
                    <div className="flex items-start">
                      <span className="text-red-400 mr-2 mt-0.5 flex-shrink-0">✗</span>
                      <div>
                        <p className="text-sm sm:text-base text-gray-800 font-medium">어디서 이탈하는지 모름</p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">"방문자는 많은데 왜 전환이 안 되지?"</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <div className="flex items-start">
                      <span className="text-red-400 mr-2 mt-0.5 flex-shrink-0">✗</span>
                      <div>
                        <p className="text-sm sm:text-base text-gray-800 font-medium">감으로 디자인 변경</p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">"이 버튼 위치가 맞는 건지 확신이 없어요"</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <div className="flex items-start">
                      <span className="text-red-400 mr-2 mt-0.5 flex-shrink-0">✗</span>
                      <div>
                        <p className="text-sm sm:text-base text-gray-800 font-medium">중요한 콘텐츠가 안 보이는 위치에</p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">"아무도 스크롤 안 하는 곳에 CTA를 넣고 있었다니..."</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 히트맵 사용 시 */}
              <div className="bg-green-50 rounded-xl p-4 sm:p-6 border-l-4 border-green-400">
                <h4 className="font-bold text-base sm:text-lg mb-3 sm:mb-4">
                  🔥 히트맵으로 분석하면
                </h4>
                <div className="space-y-3">
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2 mt-0.5 flex-shrink-0">✓</span>
                      <div>
                        <p className="text-sm sm:text-base text-gray-800 font-medium">이탈 지점을 <span className="text-green-600 font-bold">정확히</span> 파악</p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">"스크롤 히트맵으로 68%가 여기서 이탈하는 걸 발견!"</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2 mt-0.5 flex-shrink-0">✓</span>
                      <div>
                        <p className="text-sm sm:text-base text-gray-800 font-medium">데이터 기반으로 <span className="text-green-600 font-bold">확신 있는</span> 개선</p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">"클릭이 집중된 영역에 CTA를 배치하니 전환율 상승!"</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2 mt-0.5 flex-shrink-0">✓</span>
                      <div>
                        <p className="text-sm sm:text-base text-gray-800 font-medium">콘텐츠 배치를 <span className="text-green-600 font-bold">최적</span> 위치로</p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">"체류 시간이 긴 영역에 핵심 메시지를 이동!"</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 핵심 메시지 */}
            <div className="mt-6 sm:mt-8 text-center">
              <div className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-4 bg-gradient-to-r from-orange-50 to-red-50 px-4 sm:px-6 py-3 sm:py-4 rounded-xl border border-orange-200">
                <span className="text-2xl">💡</span>
                <p className="text-sm sm:text-base font-medium text-gray-800">
                  <span className="text-orange-600 font-bold">GA로는 "이탈률 70%"만 보이지만,</span>
                  <br className="sm:hidden" />
                  <span className="sm:ml-1">히트맵은 <strong>"어디서, 왜"</strong> 이탈하는지 보여줍니다</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ===== 클릭 히트맵 상세 ===== */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          {/* 헤더 */}
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-block bg-red-100 text-red-700 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              🖱️ 히트맵 1
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">
              <span className="text-red-500">클릭 히트맵</span>
            </h2>
            <p className="text-sm sm:text-lg text-gray-600">
              방문자가 어디를 클릭하는지, 어디를 무시하는지 한눈에
            </p>
          </div>

          {/* 설명 + 시각 목업 */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-8 mb-8 sm:mb-10">
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8 items-center">

              {/* 시각 목업 - 확대 버전 */}
              <div className="bg-gray-100 rounded-xl p-4 sm:p-6">
                <div className="bg-white rounded-lg shadow-sm relative overflow-hidden">
                  {/* 브라우저 바 */}
                  <div className="bg-gray-200 px-3 py-2 flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 bg-red-400 rounded-full"></div>
                    <div className="w-2.5 h-2.5 bg-yellow-400 rounded-full"></div>
                    <div className="w-2.5 h-2.5 bg-green-400 rounded-full"></div>
                    <div className="ml-2 text-[10px] text-gray-500 font-mono">mysite.com</div>
                  </div>

                  {/* 페이지 콘텐츠 */}
                  <div className="p-4 sm:p-6 space-y-4 relative min-h-[220px]">
                    {/* 네비게이션 */}
                    <div className="flex gap-3">
                      <div className="w-12 h-2.5 bg-gray-200 rounded relative">
                        <div className="absolute -inset-2 bg-yellow-400 opacity-25 rounded-full blur-sm"></div>
                      </div>
                      <div className="w-12 h-2.5 bg-gray-200 rounded relative">
                        <div className="absolute -inset-1 bg-green-400 opacity-15 rounded-full blur-sm"></div>
                      </div>
                      <div className="w-12 h-2.5 bg-gray-200 rounded"></div>
                    </div>

                    {/* 헤드라인 */}
                    <div>
                      <div className="w-40 h-4 bg-gray-300 rounded mb-2"></div>
                      <div className="w-48 h-2.5 bg-gray-200 rounded mb-1"></div>
                      <div className="w-36 h-2.5 bg-gray-200 rounded"></div>
                    </div>

                    {/* CTA 버튼 - 핫존 */}
                    <div className="relative inline-block">
                      <div className="w-28 h-9 bg-blue-500 rounded-lg text-white text-xs flex items-center justify-center font-medium">
                        지금 구매하기
                      </div>
                      <div className="absolute -inset-4 bg-red-500 opacity-35 rounded-full blur-lg"></div>
                      <div className="absolute -inset-2 bg-red-600 opacity-25 rounded-full blur-md"></div>
                    </div>

                    {/* 서브 콘텐츠 */}
                    <div className="flex gap-3 pt-2">
                      <div className="w-20 h-16 bg-gray-100 rounded relative">
                        <div className="absolute -inset-1 bg-orange-400 opacity-20 rounded-full blur-md"></div>
                      </div>
                      <div className="w-20 h-16 bg-gray-100 rounded relative">
                        <div className="absolute -inset-1 bg-yellow-300 opacity-15 rounded-full blur-sm"></div>
                      </div>
                      <div className="w-20 h-16 bg-gray-100 rounded"></div>
                    </div>
                  </div>

                  {/* 범례 */}
                  <div className="px-4 pb-3 flex items-center gap-2 text-[10px] text-gray-500">
                    <span>클릭 밀도:</span>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-red-500 opacity-60 rounded-full"></div>
                      <span>높음</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-yellow-400 opacity-50 rounded-full"></div>
                      <span>중간</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-green-400 opacity-40 rounded-full"></div>
                      <span>낮음</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 설명 */}
              <div>
                <h3 className="text-lg sm:text-xl font-bold mb-4">이런 걸 알 수 있어요</h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="w-7 h-7 bg-red-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-red-600 font-bold text-xs">1</span>
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium text-gray-800">CTA 버튼이 실제로 클릭되는지</p>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">눈에 띄는 곳에 있어도 클릭이 적을 수 있어요</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-7 h-7 bg-red-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-red-600 font-bold text-xs">2</span>
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium text-gray-800">의도하지 않은 곳에 클릭이 몰리는지</p>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">링크가 아닌 요소를 클릭하고 있다면 UX 문제!</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-7 h-7 bg-red-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-red-600 font-bold text-xs">3</span>
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium text-gray-800">어떤 콘텐츠에 관심이 높은지</p>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">클릭 패턴으로 고객 관심사를 파악할 수 있어요</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 성공 사례 */}
          <div className="bg-gradient-to-br from-red-50 to-white rounded-xl p-4 sm:p-6 border border-red-200">
            <div className="flex items-center mb-3 sm:mb-4">
              <span className="text-xl sm:text-2xl mr-2">📈</span>
              <h4 className="font-bold text-base sm:text-lg">클릭 히트맵 활용 사례</h4>
            </div>
            <div className="bg-white rounded-lg p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
                <p className="font-bold text-gray-800 text-sm sm:text-base">🛒 이커머스 A사 — 상품 상세 페이지</p>
                <span className="text-green-600 font-bold text-lg mt-1 sm:mt-0">클릭률 +42%</span>
              </div>
              <div className="space-y-2 text-sm sm:text-base text-gray-600">
                <p><strong>발견:</strong> "구매하기" 버튼보다 상품 이미지에 클릭이 3배 집중</p>
                <p><strong>원인:</strong> 방문자가 이미지를 확대하려고 클릭하지만 기능이 없었음</p>
                <p><strong>개선:</strong> 이미지 확대 기능 추가 + 확대 뷰 내 구매 버튼 배치</p>
                <p className="text-green-600 font-medium pt-1">→ 구매 버튼 클릭률 42% 증가</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 스크롤 히트맵 상세 ===== */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          {/* 헤더 */}
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-block bg-yellow-100 text-yellow-700 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              📜 히트맵 2
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">
              <span className="text-yellow-600">스크롤 히트맵</span>
            </h2>
            <p className="text-sm sm:text-lg text-gray-600">
              방문자가 어디까지 보는지, 어디서 떠나는지 정확히
            </p>
          </div>

          {/* 설명 + 시각 목업 */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-8 mb-8 sm:mb-10">
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8 items-center">

              {/* 시각 목업 */}
              <div className="bg-gray-100 rounded-xl p-4 sm:p-6">
                <div className="bg-white rounded-lg shadow-sm relative overflow-hidden">
                  {/* 브라우저 바 */}
                  <div className="bg-gray-200 px-3 py-2 flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 bg-red-400 rounded-full"></div>
                    <div className="w-2.5 h-2.5 bg-yellow-400 rounded-full"></div>
                    <div className="w-2.5 h-2.5 bg-green-400 rounded-full"></div>
                    <div className="ml-2 text-[10px] text-gray-500 font-mono">mysite.com/landing</div>
                  </div>

                  {/* 페이지 + 스크롤 오버레이 */}
                  <div className="relative min-h-[260px]" style={{
                    background: 'linear-gradient(to bottom, rgba(239,68,68,0.3) 0%, rgba(249,115,22,0.25) 20%, rgba(234,179,8,0.2) 40%, rgba(34,197,94,0.15) 60%, rgba(59,130,246,0.1) 80%, rgba(99,102,241,0.05) 100%)'
                  }}>
                    <div className="p-4 sm:p-6 space-y-4">
                      {/* 섹션 1: 히어로 */}
                      <div>
                        <div className="w-32 h-4 bg-gray-300 rounded mb-2 opacity-60"></div>
                        <div className="w-40 h-2.5 bg-gray-200 rounded mb-1 opacity-60"></div>
                        <div className="w-24 h-7 bg-blue-500 rounded mt-2 opacity-60"></div>
                      </div>

                      {/* 구분선 + 퍼센트 */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 border-t-2 border-dashed border-red-400 opacity-60"></div>
                        <span className="text-xs font-bold text-red-500 bg-white px-2 py-0.5 rounded">100%</span>
                      </div>

                      {/* 섹션 2 */}
                      <div>
                        <div className="w-28 h-3 bg-gray-300 rounded mb-2 opacity-50"></div>
                        <div className="w-36 h-2 bg-gray-200 rounded mb-1 opacity-50"></div>
                        <div className="w-32 h-2 bg-gray-200 rounded opacity-50"></div>
                      </div>

                      {/* 구분선 + 퍼센트 */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 border-t-2 border-dashed border-yellow-500 opacity-60"></div>
                        <span className="text-xs font-bold text-yellow-600 bg-white px-2 py-0.5 rounded">62%</span>
                      </div>

                      {/* 섹션 3 */}
                      <div>
                        <div className="w-24 h-3 bg-gray-300 rounded mb-2 opacity-40"></div>
                        <div className="w-32 h-2 bg-gray-200 rounded mb-1 opacity-40"></div>
                      </div>

                      {/* 구분선 + 퍼센트 */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 border-t-2 border-dashed border-blue-400 opacity-60"></div>
                        <span className="text-xs font-bold text-blue-500 bg-white px-2 py-0.5 rounded">28%</span>
                      </div>

                      {/* 섹션 4: 거의 안 보는 영역 */}
                      <div>
                        <div className="w-20 h-2.5 bg-gray-200 rounded mb-1 opacity-30"></div>
                        <div className="w-28 h-2 bg-gray-200 rounded opacity-30"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 설명 */}
              <div>
                <h3 className="text-lg sm:text-xl font-bold mb-4">이런 걸 알 수 있어요</h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="w-7 h-7 bg-yellow-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-yellow-600 font-bold text-xs">1</span>
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium text-gray-800">방문자의 몇 %가 어디까지 보는지</p>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">페이지 하단까지 도달하는 비율을 정확히 확인</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-7 h-7 bg-yellow-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-yellow-600 font-bold text-xs">2</span>
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium text-gray-800">이탈이 급격히 늘어나는 지점</p>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">콘텐츠가 지루해지거나 관련 없는 구간 발견</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-7 h-7 bg-yellow-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-yellow-600 font-bold text-xs">3</span>
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium text-gray-800">중요한 콘텐츠가 적절한 위치에 있는지</p>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">CTA나 핵심 메시지가 대부분이 보는 영역 안에 있는지 확인</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 성공 사례 */}
          <div className="bg-gradient-to-br from-yellow-50 to-white rounded-xl p-4 sm:p-6 border border-yellow-200">
            <div className="flex items-center mb-3 sm:mb-4">
              <span className="text-xl sm:text-2xl mr-2">📈</span>
              <h4 className="font-bold text-base sm:text-lg">스크롤 히트맵 활용 사례</h4>
            </div>
            <div className="bg-white rounded-lg p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
                <p className="font-bold text-gray-800 text-sm sm:text-base">📚 교육 플랫폼 B사 — 강의 소개 페이지</p>
                <span className="text-green-600 font-bold text-lg mt-1 sm:mt-0">신청률 +35%</span>
              </div>
              <div className="space-y-2 text-sm sm:text-base text-gray-600">
                <p><strong>발견:</strong> 방문자의 72%가 수강 후기 섹션 전에 이탈</p>
                <p><strong>원인:</strong> 커리큘럼 설명이 너무 길어 핵심 정보(후기, 가격)까지 도달 못함</p>
                <p><strong>개선:</strong> 수강 후기를 페이지 상단으로 이동 + 커리큘럼 접기 처리</p>
                <p className="text-green-600 font-medium pt-1">→ 강의 신청률 35% 증가</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 어텐션 히트맵 상세 ===== */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          {/* 헤더 */}
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-block bg-purple-100 text-purple-700 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              👁️ 히트맵 3
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">
              <span className="text-purple-600">체류시간 히트맵</span>
            </h2>
            <p className="text-sm sm:text-lg text-gray-600">
              방문자가 어디에서 오래 머무르는지, 진짜 관심 영역을 파악
            </p>
          </div>

          {/* 설명 + 시각 목업 */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-8 mb-8 sm:mb-10">
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8 items-center">

              {/* 시각 목업 */}
              <div className="bg-gray-100 rounded-xl p-4 sm:p-6">
                <div className="bg-white rounded-lg shadow-sm relative overflow-hidden">
                  {/* 브라우저 바 */}
                  <div className="bg-gray-200 px-3 py-2 flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 bg-red-400 rounded-full"></div>
                    <div className="w-2.5 h-2.5 bg-yellow-400 rounded-full"></div>
                    <div className="w-2.5 h-2.5 bg-green-400 rounded-full"></div>
                    <div className="ml-2 text-[10px] text-gray-500 font-mono">mysite.com/pricing</div>
                  </div>

                  {/* 페이지 콘텐츠 */}
                  <div className="p-4 sm:p-6 space-y-3 relative min-h-[260px]">
                    {/* 헤드라인 */}
                    <div className="relative">
                      <div className="w-32 h-4 bg-gray-300 rounded mb-2"></div>
                      <div className="w-40 h-2.5 bg-gray-200 rounded"></div>
                      <div className="absolute -inset-2 bg-orange-400 opacity-15 rounded blur-md"></div>
                      <div className="absolute -right-1 top-0 bg-orange-500 text-white text-[7px] px-1.5 py-0.5 rounded font-bold">5초</div>
                    </div>

                    {/* 가격 섹션 - 핫존 */}
                    <div className="relative bg-gray-50 rounded-lg p-3">
                      <div className="flex gap-2 mb-2">
                        <div className="w-16 h-12 bg-blue-100 rounded"></div>
                        <div className="w-16 h-12 bg-green-100 rounded"></div>
                        <div className="w-16 h-12 bg-purple-100 rounded"></div>
                      </div>
                      <div className="w-24 h-2 bg-gray-200 rounded"></div>
                      {/* 강한 어텐션 오버레이 */}
                      <div className="absolute -inset-1 bg-red-500 opacity-20 rounded-lg blur-sm"></div>
                      <div className="absolute -inset-0.5 bg-red-600 opacity-15 rounded-lg blur-md"></div>
                      <div className="absolute -right-1 -top-1 bg-red-500 text-white text-[7px] px-1.5 py-0.5 rounded font-bold">14초</div>
                    </div>

                    {/* FAQ 섹션 */}
                    <div className="relative">
                      <div className="w-24 h-3 bg-gray-300 rounded mb-2"></div>
                      <div className="w-36 h-2 bg-gray-200 rounded mb-1"></div>
                      <div className="w-32 h-2 bg-gray-200 rounded"></div>
                      <div className="absolute -inset-1 bg-yellow-400 opacity-10 rounded blur-sm"></div>
                      <div className="absolute -right-1 top-0 bg-yellow-500 text-white text-[7px] px-1.5 py-0.5 rounded font-bold">3초</div>
                    </div>

                    {/* 하단 콘텐츠 */}
                    <div className="relative">
                      <div className="w-28 h-2.5 bg-gray-200 rounded mb-1"></div>
                      <div className="w-20 h-2 bg-gray-200 rounded"></div>
                      <div className="absolute -right-1 top-0 bg-gray-400 text-white text-[7px] px-1.5 py-0.5 rounded font-bold">1초</div>
                    </div>
                  </div>

                  {/* 범례 */}
                  <div className="px-4 pb-3 flex items-center gap-2 text-[10px] text-gray-500">
                    <span>체류 시간:</span>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-red-500 opacity-50 rounded"></div>
                      <span>길다</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-yellow-400 opacity-40 rounded"></div>
                      <span>보통</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-gray-300 opacity-40 rounded"></div>
                      <span>짧다</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 설명 */}
              <div>
                <h3 className="text-lg sm:text-xl font-bold mb-4">이런 걸 알 수 있어요</h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="w-7 h-7 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-purple-600 font-bold text-xs">1</span>
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium text-gray-800">방문자가 진짜 관심 있는 영역</p>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">오래 머무는 곳 = 관심 있거나, 이해하기 어려운 곳</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-7 h-7 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-purple-600 font-bold text-xs">2</span>
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium text-gray-800">빠르게 지나치는 무관심 영역</p>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">공들인 콘텐츠인데 아무도 안 읽는다면 위치 변경 필요</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-7 h-7 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-purple-600 font-bold text-xs">3</span>
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium text-gray-800">구매 결정 전 고민하는 구간</p>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">가격 영역에서 오래 머문다면 가격 전략 재검토 신호</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 성공 사례 */}
          <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-4 sm:p-6 border border-purple-200">
            <div className="flex items-center mb-3 sm:mb-4">
              <span className="text-xl sm:text-2xl mr-2">📈</span>
              <h4 className="font-bold text-base sm:text-lg">체류시간 히트맵 활용 사례</h4>
            </div>
            <div className="bg-white rounded-lg p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
                <p className="font-bold text-gray-800 text-sm sm:text-base">💼 SaaS 기업 C사 — 요금제 페이지</p>
                <span className="text-green-600 font-bold text-lg mt-1 sm:mt-0">결제 전환 +28%</span>
              </div>
              <div className="space-y-2 text-sm sm:text-base text-gray-600">
                <p><strong>발견:</strong> 가격 비교표에서 평균 14초 체류, FAQ는 1초만 스쳐지나감</p>
                <p><strong>원인:</strong> 요금제 차이가 불명확해 고객이 비교하느라 시간 소모</p>
                <p><strong>개선:</strong> 요금제별 핵심 차이점 강조 + "가장 인기" 배지 추가</p>
                <p className="text-green-600 font-medium pt-1">→ 가격 페이지 체류 시간 감소(-40%) + 결제 전환 28% 증가</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          {/* 2가지 활용 방식 */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-8 mb-8 sm:mb-12">
            <h3 className="text-xl sm:text-2xl font-bold text-center mb-6 sm:mb-8">
              <span className="text-orange-600">2가지 방식</span>으로 활용하세요
            </h3>

            <div className="grid md:grid-cols-2 gap-4 sm:gap-6">

              {/* 방식 1: 독립 히트맵 */}
              <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-4 sm:p-6 border-2 border-orange-200 relative">
                <div className="absolute -top-3 left-4 bg-orange-500 text-white text-xs px-3 py-1 rounded-full font-bold">
                  지금 바로 시작
                </div>

                <div className="flex items-center mb-3 sm:mb-4 mt-1">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-lg">🔍</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-base sm:text-lg">독립 히트맵 분석</h4>
                    <p className="text-xs sm:text-sm text-gray-500">테스트 없이 바로 분석</p>
                  </div>
                </div>

                <div className="space-y-2 sm:space-y-3 mb-4">
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 mt-0.5">✓</span>
                    <p className="text-sm text-gray-700">URL만 입력하면 <strong>즉시 데이터 수집</strong> 시작</p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 mt-0.5">✓</span>
                    <p className="text-sm text-gray-700">현재 사이트의 <strong>문제점을 먼저 파악</strong></p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 mt-0.5">✓</span>
                    <p className="text-sm text-gray-700">히트맵 데이터 기반으로 <strong>개선안 도출</strong></p>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-3 border border-orange-100">
                  <p className="text-xs sm:text-sm text-gray-600">
                    <span className="font-bold text-orange-600">💡 추천:</span> "어디를 고쳐야 할지 모를 때" 먼저 히트맵으로 현황 파악 → 그 다음 A/B 테스트
                  </p>
                </div>
              </div>

              {/* 방식 2: A/B 테스트 + 히트맵 */}
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-4 sm:p-6 border-2 border-blue-200 relative">
                <div className="absolute -top-3 left-4 bg-blue-600 text-white text-xs px-3 py-1 rounded-full font-bold">
                  최대 효과
                </div>

                <div className="flex items-center mb-3 sm:mb-4 mt-1">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-lg">🚀</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-base sm:text-lg">A/B 테스트 + 히트맵</h4>
                    <p className="text-xs sm:text-sm text-gray-500">테스트와 동시에 분석</p>
                  </div>
                </div>

                <div className="space-y-2 sm:space-y-3 mb-4">
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 mt-0.5">✓</span>
                    <p className="text-sm text-gray-700">A/B 테스트 실행 시 <strong>자동으로 히트맵 수집</strong></p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 mt-0.5">✓</span>
                    <p className="text-sm text-gray-700">A안과 B안의 <strong>행동 차이를 비교</strong></p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 mt-0.5">✓</span>
                    <p className="text-sm text-gray-700">"왜 B안이 더 좋은지" <strong>원인까지 파악</strong></p>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-3 border border-blue-100">
                  <p className="text-xs sm:text-sm text-gray-600">
                    <span className="font-bold text-blue-600">💡 결과:</span> "B안이 25% 좋다" → "B안의 CTA에 클릭 3배 집중" 까지 한번에
                  </p>
                </div>
              </div>
            </div>

            {/* 추천 워크플로우 */}
            <div className="mt-6 sm:mt-8">
              <div className="bg-gray-50 rounded-lg p-4 sm:p-5">
                <p className="text-xs sm:text-sm text-gray-500 text-center mb-3 font-medium">추천 워크플로우</p>

                {/* 모바일: 세로 */}
                <div className="sm:hidden flex flex-col items-center gap-2">
                  <div className="bg-purple-100 text-purple-700 px-4 py-2 rounded-lg text-sm font-bold text-center w-full">
                    ① 퍼널 분석으로 이탈 단계 발견
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-11a1 1 0 112 0v3.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L9 10.586V7z" clipRule="evenodd" />
                  </svg>
                  <div className="bg-orange-100 text-orange-700 px-4 py-2 rounded-lg text-sm font-bold text-center w-full">
                    ② 히트맵으로 이탈 경향 파악
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-11a1 1 0 112 0v3.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L9 10.586V7z" clipRule="evenodd" />
                  </svg>
                  <div className="bg-cyan-100 text-cyan-700 px-4 py-2 rounded-lg text-sm font-bold text-center w-full">
                    ③ 세션 녹화로 이탈 장면 확인
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-11a1 1 0 112 0v3.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L9 10.586V7z" clipRule="evenodd" />
                  </svg>
                  <div className="bg-blue-100 text-blue-700 px-4 py-2 rounded-lg text-sm font-bold text-center w-full">
                    ④ A/B 테스트로 개선안 검증
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-11a1 1 0 112 0v3.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L9 10.586V7z" clipRule="evenodd" />
                  </svg>
                  <div className="bg-green-100 text-green-700 px-4 py-2 rounded-lg text-sm font-bold text-center w-full">
                    ⑤ 히트맵으로 결과 원인 분석
                  </div>
                </div>

                {/* 데스크톱: 가로 */}
                <div className="hidden sm:flex flex-wrap items-center justify-center gap-2">
                  <div className="bg-purple-100 text-purple-700 px-3 py-2 rounded-lg text-sm font-bold">
                    ① 퍼널로 이탈 발견
                  </div>
                  <span className="text-gray-400 text-lg">→</span>
                  <div className="bg-orange-100 text-orange-700 px-3 py-2 rounded-lg text-sm font-bold">
                    ② 히트맵으로 경향 파악
                  </div>
                  <span className="text-gray-400 text-lg">→</span>
                  <div className="bg-cyan-100 text-cyan-700 px-3 py-2 rounded-lg text-sm font-bold">
                    ③ 세션 녹화로 장면 확인
                  </div>
                  <span className="text-gray-400 text-lg">→</span>
                  <div className="bg-blue-100 text-blue-700 px-3 py-2 rounded-lg text-sm font-bold">
                    ④ A/B 테스트로 검증
                  </div>
                  <span className="text-gray-400 text-lg">→</span>
                  <div className="bg-green-100 text-green-700 px-3 py-2 rounded-lg text-sm font-bold">
                    ⑤ 결과 원인 분석
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 하단 강조 */}
          <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-4 sm:p-6 rounded-lg shadow-xl text-center">
            <div className="inline-flex items-center">
              <span className="text-2xl sm:text-3xl mr-3">⚡</span>
              <div className="text-left">
                <span className="text-lg sm:text-2xl font-bold block">
                  URL 입력만으로 바로 시작
                </span>
                <span className="text-xs sm:text-sm opacity-90">
                  독립 분석도, A/B 테스트 연동도 추가 설정 없이 바로 사용 가능합니다
                </span>
              </div>
            </div>
          </div>
        </div>
      </section >

      {/* ===== 퍼널 분석 섹션 ===== */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          {/* 헤더 */}
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-block bg-purple-100 text-purple-700 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              📊 신규 기능
            </div>

            <div className="sm:hidden mb-3">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-100 to-violet-200 rounded-full">
                <span className="text-2xl">📊</span>
              </div>
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">
              고객이 <span className="text-purple-600">어느 단계에서 이탈</span>하는지
              <br className="sm:hidden" /> 한눈에 파악하세요
            </h2>
            <p className="text-sm sm:text-lg text-gray-600">
              전환율 3%라는 숫자만으로는 부족합니다.
              <br className="sm:hidden" /> 단계별 이탈 지점을 시각화하세요.
            </p>
          </div>

          {/* 퍼널 시각화 + 핵심 포인트 */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-8 border border-gray-200 mb-8 sm:mb-12">
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8 items-center">

              {/* 퍼널 시각화 */}
              <div className="bg-gray-50 rounded-xl p-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h4 className="font-bold text-gray-800 text-sm sm:text-base">구매 전환 퍼널</h4>
                    <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5">최근 30일 • 10,000명 추적</p>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                    <span className="text-gray-500">실시간</span>
                  </div>
                </div>

                <div className="space-y-2.5">
                  {[
                    { label: '페이지 방문', value: '10,000', width: '100%', color: 'bg-purple-500', drop: null },
                    { label: '상품 조회', value: '6,200', width: '62%', color: 'bg-purple-500', drop: '-38%' },
                    { label: '장바구니', value: '2,480', width: '24.8%', color: 'bg-violet-500', drop: '-60%' },
                    { label: '결제 시작', value: '1,240', width: '12.4%', color: 'bg-violet-600', drop: '-50%' },
                    { label: '구매 완료', value: '310', width: '3.1%', color: 'bg-pink-500', drop: '-75%' },
                  ].map((step, idx) => (
                    <div key={idx}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs text-gray-600 font-medium">{step.label}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-gray-800">{step.value}</span>
                          {step.drop && (
                            <span className="text-[10px] text-red-500 font-medium">{step.drop}</span>
                          )}
                        </div>
                      </div>
                      <div className="h-6 bg-gray-200 rounded-md overflow-hidden">
                        <div className={`h-full ${step.color} rounded-md`} style={{ width: step.width, opacity: 0.85 }}></div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="pt-3 mt-3 border-t border-gray-200 flex justify-between items-center">
                  <span className="text-xs text-gray-400">전체 전환율</span>
                  <span className="text-lg font-bold text-purple-600">3.1%</span>
                </div>
              </div>

              {/* 핵심 포인트 */}
              <div>
                <h3 className="text-lg sm:text-xl font-bold mb-4">이런 걸 알 수 있어요</h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="w-7 h-7 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-purple-600 font-bold text-xs">1</span>
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium text-gray-800">어느 단계에서 가장 많이 이탈하는지</p>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">병목 구간을 찾아 우선 개선 포인트 결정</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-7 h-7 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-purple-600 font-bold text-xs">2</span>
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium text-gray-800">단계 간 전환에 얼마나 걸리는지</p>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">전환 시간이 길면 UX에 마찰이 있다는 신호</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-7 h-7 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-purple-600 font-bold text-xs">3</span>
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium text-gray-800">히트맵·세션 녹화와 연동하여 이탈 원인까지 파악</p>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">퍼널에서 병목 발견 → 해당 페이지 히트맵·녹화로 원인 분석</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 문제/이점 비교 */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-8 mb-8 sm:mb-12">
            <div className="text-center mb-6 sm:mb-8">
              <h3 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3">
                <span className="text-red-500">전환율 숫자만</span> 보고 있지 않나요?
              </h3>
              <p className="text-sm sm:text-base text-gray-500">
                전체 전환율로는 어디가 문제인지 절대 알 수 없습니다
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
              {/* 퍼널 없이 */}
              <div className="bg-red-50 rounded-xl p-4 sm:p-6 border-l-4 border-red-400">
                <h4 className="font-bold text-base sm:text-lg mb-3 sm:mb-4">
                  😰 전환율만 보면
                </h4>
                <div className="space-y-3">
                  <div className="bg-white rounded-lg p-3">
                    <div className="flex items-start">
                      <span className="text-red-400 mr-2 mt-0.5 flex-shrink-0">✗</span>
                      <div>
                        <p className="text-sm sm:text-base text-gray-800 font-medium">"전환율 3%입니다"</p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">그래서 나머지 97%는 어디서 빠진 거죠?</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <div className="flex items-start">
                      <span className="text-red-400 mr-2 mt-0.5 flex-shrink-0">✗</span>
                      <div>
                        <p className="text-sm sm:text-base text-gray-800 font-medium">"이번 달 매출이 줄었습니다"</p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">어느 단계가 무너진 건지 알 수 없음</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <div className="flex items-start">
                      <span className="text-red-400 mr-2 mt-0.5 flex-shrink-0">✗</span>
                      <div>
                        <p className="text-sm sm:text-base text-gray-800 font-medium">"뭘 고쳐야 할지 모르겠어요"</p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">감으로 개선하다 시간과 비용만 낭비</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 퍼널 분석 사용 시 */}
              <div className="bg-green-50 rounded-xl p-4 sm:p-6 border-l-4 border-green-400">
                <h4 className="font-bold text-base sm:text-lg mb-3 sm:mb-4">
                  📊 퍼널로 분석하면
                </h4>
                <div className="space-y-3">
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2 mt-0.5 flex-shrink-0">✓</span>
                      <div>
                        <p className="text-sm sm:text-base text-gray-800 font-medium">"장바구니→결제에서 <span className="text-green-600 font-bold">62% 이탈</span>"</p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">정확한 병목 구간을 즉시 발견</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2 mt-0.5 flex-shrink-0">✓</span>
                      <div>
                        <p className="text-sm sm:text-base text-gray-800 font-medium">"결제 단계 전환에 <span className="text-green-600 font-bold">4분 소요</span>"</p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">UX 마찰 구간을 시간 데이터로 확인</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2 mt-0.5 flex-shrink-0">✓</span>
                      <div>
                        <p className="text-sm sm:text-base text-gray-800 font-medium">"결제 UX 개선이 <span className="text-green-600 font-bold">최우선 과제</span>"</p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">데이터 기반으로 가장 임팩트 큰 곳부터 개선</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 sm:mt-8 text-center">
              <div className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-4 bg-gradient-to-r from-purple-50 to-violet-50 px-4 sm:px-6 py-3 sm:py-4 rounded-xl border border-purple-200">
                <span className="text-2xl">💡</span>
                <p className="text-sm sm:text-base font-medium text-gray-800">
                  <span className="text-purple-600 font-bold">GA로는 "전환율 3%"만 보이지만,</span>
                  <br className="sm:hidden" />
                  <span className="sm:ml-1">퍼널 분석은 <strong>"어디서, 얼마나"</strong> 이탈하는지 보여줍니다</span>
                </p>
              </div>
            </div>
          </div>

          {/* 성공 사례 */}
          <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-4 sm:p-6 border border-purple-200">
            <div className="flex items-center mb-3 sm:mb-4">
              <span className="text-xl sm:text-2xl mr-2">📈</span>
              <h4 className="font-bold text-base sm:text-lg">퍼널 분석 활용 사례</h4>
            </div>
            <div className="bg-white rounded-lg p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
                <p className="font-bold text-gray-800 text-sm sm:text-base">🛒 이커머스 — 구매 전환 퍼널 최적화</p>
                <span className="text-green-600 font-bold text-lg mt-1 sm:mt-0">구매 전환율 +40%</span>
              </div>
              <div className="space-y-2 text-sm sm:text-base text-gray-600">
                <p><strong>발견:</strong> 장바구니→결제 단계에서 60%가 이탈, 전환 시간도 4분 이상 소요</p>
                <p><strong>원인:</strong> 결제 페이지에서 배송비가 뒤늦게 표시되어 이탈 유발</p>
                <p><strong>개선:</strong> 장바구니 단계에서 배송비 미리 표시 + 무료배송 조건 안내</p>
                <p className="text-green-600 font-medium pt-1">→ 결제 이탈률 60% → 35%로 감소, 구매 전환율 40% 상승</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 세션 녹화 섹션 ===== */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          {/* 헤더 */}
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-block bg-cyan-100 text-cyan-700 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              🎬 신규 기능
            </div>

            <div className="sm:hidden mb-3">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-100 to-sky-200 rounded-full">
                <span className="text-2xl">🎬</span>
              </div>
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">
              고객이 <span className="text-cyan-600">떠나던 순간</span>을
              <br className="sm:hidden" /> 화면 그대로 다시 보세요
            </h2>
            <p className="text-sm sm:text-lg text-gray-600">
              숫자는 '얼마나' 이탈했는지만 알려줍니다.
              <br className="sm:hidden" /> 세션 녹화는 '왜'를 보여줍니다.
            </p>
          </div>

          {/* 플레이어 시각화 + 핵심 포인트 */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-8 border border-gray-200 mb-8 sm:mb-12">
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8 items-center">

              {/* 재생 화면 목업 */}
              <div className="bg-gray-900 rounded-xl overflow-hidden">
                <div className="px-4 py-2.5 flex items-center justify-between border-b border-gray-800">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-500 to-sky-600 flex items-center justify-center text-[9px] font-bold text-white">수</span>
                    <div>
                      <div className="text-[11px] font-bold text-white">차분한 수달 <span className="text-gray-500 font-mono font-normal">· m9x7</span></div>
                      <div className="text-[9px] text-gray-500">모바일 · Safari · 네이버 검색 유입</div>
                    </div>
                  </div>
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded bg-red-500/10 border border-red-500/20 text-[9px] font-bold text-red-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>REC
                  </span>
                </div>

                <div className="p-3">
                  <div className="relative h-40 sm:h-44 bg-white rounded-lg overflow-hidden">
                    <div className="h-5 bg-gray-100 border-b border-gray-200 flex items-center px-2 gap-1.5">
                      <span className="w-3 h-3 rounded bg-cyan-500"></span>
                      <span className="w-12 h-1.5 rounded bg-gray-300"></span>
                    </div>
                    <div className="p-2.5 grid grid-cols-5 gap-2">
                      <div className="col-span-2 h-20 rounded bg-gradient-to-br from-gray-100 to-gray-200"></div>
                      <div className="col-span-3 space-y-1.5">
                        <div className="w-3/4 h-2 rounded bg-gray-700"></div>
                        <div className="w-1/2 h-1.5 rounded bg-gray-300"></div>
                        <div className="mt-2 h-5 rounded border border-red-300 bg-red-50/60 flex items-center justify-between px-1.5">
                          <span className="w-10 h-1 rounded bg-gray-400"></span>
                          <span className="text-[8px] text-gray-400">▾</span>
                        </div>
                        <div className="h-5 rounded bg-gray-900"></div>
                      </div>
                    </div>
                    {/* 커서 + 클릭 자국 */}
                    <span className="absolute left-[62%] top-[48%] -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 border-cyan-500 animate-ping"></span>
                    <span className="absolute left-[62%] top-[48%] text-gray-900 text-sm" style={{ transform: 'translate(2px, 2px)' }}>➤</span>
                    {/* 이탈 직전 칩 */}
                    <div className="absolute bottom-2 right-2 bg-gray-900/90 text-white px-2.5 py-1.5 rounded-lg border border-red-500/30">
                      <div className="text-[8px] font-bold text-red-400 uppercase">이탈 직전</div>
                      <div className="text-[10px] font-bold">옵션 선택 3회 반복</div>
                    </div>
                  </div>

                  {/* 타임라인 */}
                  <div className="mt-3 relative h-1.5 bg-gray-800 rounded-full">
                    <div className="absolute left-0 w-[31%] h-full bg-gradient-to-r from-cyan-500 to-sky-500 rounded-full"></div>
                    <div className="absolute left-[46%] w-[16%] h-full bg-gray-700 rounded-full"></div>
                    {['18%', '26%', '31%', '40%', '78%', '90%'].map((l, i) => (
                      <span key={i} className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-cyan-300 ring-2 ring-gray-900" style={{ left: l }}></span>
                    ))}
                    <span className="absolute left-[31%] top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-white ring-2 ring-cyan-500"></span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[10px]">
                    <span className="font-mono text-gray-300">0:31 <span className="text-gray-600">/ 1:52</span></span>
                    <div className="flex items-center gap-1.5">
                      <span className="px-1.5 py-0.5 rounded bg-gray-800 font-bold text-gray-300">2x</span>
                      <span className="px-1.5 py-0.5 rounded bg-cyan-500/15 font-bold text-cyan-400">⏭ 비활동 건너뛰기</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 핵심 포인트 */}
              <div>
                <h3 className="text-lg sm:text-xl font-bold mb-4">이런 걸 볼 수 있어요</h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="w-7 h-7 bg-cyan-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-cyan-600 font-bold text-xs">1</span>
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium text-gray-800">방문자가 본 화면을 그대로 재생</p>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">마우스 이동 · 클릭 · 스크롤 · 입력 · 페이지 이동까지</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-7 h-7 bg-cyan-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-cyan-600 font-bold text-xs">2</span>
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium text-gray-800">어디서 헤매고 무엇에 막혔는지</p>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">같은 버튼 반복 클릭, 안 열리는 메뉴, 가려진 버튼을 직접 확인</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-7 h-7 bg-cyan-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-cyan-600 font-bold text-xs">3</span>
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium text-gray-800">봐야 할 세션만 골라서</p>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">디바이스 · 세션 길이 · 방문 페이지로 필터, 비활동 구간은 자동 건너뛰기</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 문제/이점 비교 */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-8 mb-8 sm:mb-12">
            <div className="text-center mb-6 sm:mb-8">
              <h3 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3">
                <span className="text-red-500">집계 숫자만</span> 보고 추측하고 있지 않나요?
              </h3>
              <p className="text-sm sm:text-base text-gray-500">
                평균값에는 '한 사람'이 없습니다
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
              {/* 녹화 없이 */}
              <div className="bg-red-50 rounded-xl p-4 sm:p-6 border-l-4 border-red-400">
                <h4 className="font-bold text-base sm:text-lg mb-3 sm:mb-4">
                  😰 숫자만 보면
                </h4>
                <div className="space-y-3">
                  <div className="bg-white rounded-lg p-3">
                    <div className="flex items-start">
                      <span className="text-red-400 mr-2 mt-0.5 flex-shrink-0">✗</span>
                      <div>
                        <p className="text-sm sm:text-base text-gray-800 font-medium">"옵션 영역 클릭률이 높아요"</p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">많이 눌렀는데 왜 전환이 안 되죠?</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <div className="flex items-start">
                      <span className="text-red-400 mr-2 mt-0.5 flex-shrink-0">✗</span>
                      <div>
                        <p className="text-sm sm:text-base text-gray-800 font-medium">"모바일 이탈률이 PC의 2배예요"</p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">모바일에서 무슨 일이 있는지 알 수 없음</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <div className="flex items-start">
                      <span className="text-red-400 mr-2 mt-0.5 flex-shrink-0">✗</span>
                      <div>
                        <p className="text-sm sm:text-base text-gray-800 font-medium">"화면이 이상하다는 문의가 와요"</p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">재현이 안 돼서 며칠째 원인 못 찾음</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 녹화로 보면 */}
              <div className="bg-green-50 rounded-xl p-4 sm:p-6 border-l-4 border-green-400">
                <h4 className="font-bold text-base sm:text-lg mb-3 sm:mb-4">
                  🎬 세션을 재생하면
                </h4>
                <div className="space-y-3">
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2 mt-0.5 flex-shrink-0">✓</span>
                      <div>
                        <p className="text-sm sm:text-base text-gray-800 font-medium">"드롭다운이 안 열려 <span className="text-green-600 font-bold">3번 두드리고 이탈</span>"</p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">클릭률이 높았던 진짜 이유</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2 mt-0.5 flex-shrink-0">✓</span>
                      <div>
                        <p className="text-sm sm:text-base text-gray-800 font-medium">"인앱 브라우저에서 <span className="text-green-600 font-bold">구매 버튼이 가려짐</span>"</p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">모바일 이탈의 구체적인 장면</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2 mt-0.5 flex-shrink-0">✓</span>
                      <div>
                        <p className="text-sm sm:text-base text-gray-800 font-medium">"해당 세션 재생 → <span className="text-green-600 font-bold">수 분 만에 재현</span>"</p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">디바이스·브라우저 필터로 문제 세션만 바로 찾기</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 sm:mt-8 text-center">
              <div className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-4 bg-gradient-to-r from-cyan-50 to-sky-50 px-4 sm:px-6 py-3 sm:py-4 rounded-xl border border-cyan-200">
                <span className="text-2xl">🔒</span>
                <p className="text-sm sm:text-base font-medium text-gray-800">
                  <span className="text-cyan-600 font-bold">입력값은 전부 가려진 채로만 저장됩니다.</span>
                  <br className="sm:hidden" />
                  <span className="sm:ml-1">마스킹은 <strong>끌 수 없어서</strong> 개인정보 걱정 없이 쓸 수 있어요</span>
                </p>
              </div>
            </div>
          </div>

          {/* 성공 사례 */}
          <div className="bg-gradient-to-br from-cyan-50 to-white rounded-xl p-4 sm:p-6 border border-cyan-200">
            <div className="flex items-center mb-3 sm:mb-4">
              <span className="text-xl sm:text-2xl mr-2">🎬</span>
              <h4 className="font-bold text-base sm:text-lg">세션 녹화 활용 사례</h4>
            </div>
            <div className="bg-white rounded-lg p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
                <p className="font-bold text-gray-800 text-sm sm:text-base">📱 이커머스 — 모바일 옵션 선택 실패</p>
                <span className="text-green-600 font-bold text-lg mt-1 sm:mt-0">장바구니 전환 1.6배</span>
              </div>
              <div className="space-y-2 text-sm sm:text-base text-gray-600">
                <p><strong>발견:</strong> 히트맵에서 옵션 영역 클릭은 많은데 장바구니 담기로 이어지지 않음</p>
                <p><strong>원인:</strong> 모바일 세션을 재생해 보니 옵션 드롭다운이 키보드에 가려 3~4번 반복 클릭 후 이탈</p>
                <p><strong>개선:</strong> 옵션 선택을 드롭다운에서 버튼형 선택지로 교체</p>
                <p className="text-green-600 font-medium pt-1">→ 모바일 장바구니 전환율 1.6배 상승, 옵션 관련 문의 감소</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 걱정 해소 섹션 - 모바일 최적화 */}
      < section className="py-12 sm:py-20 bg-gradient-to-br from-blue-50 to-white" >
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-block bg-red-100 text-red-700 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              💡 많은 분들이 걱정하시는 부분
            </div>
            <h2 className="text-2xl sm:text-4xl font-bold mb-3 sm:mb-4">
              "설치가 복잡하면 어쩌지?"
              <br />
              <span className="text-blue-600">걱정하지 마세요!</span>
            </h2>
          </div>

          {/* 무료 지원 서비스 상세 - 모바일 최적화 */}
          <div className="bg-white rounded-xl shadow-xl p-4 sm:p-8 border-2 border-blue-500">
            <div className="text-center mb-6 sm:mb-8">
              <div className="inline-block bg-blue-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-2 sm:mb-3">
                30일 무료 기간 동안
              </div>
              <h3 className="text-lg sm:text-2xl font-bold">
                이 모든 걸 <span className="text-blue-600">무료로</span>{" "}
                해드립니다
              </h3>
            </div>

            {/* 모바일: 1열, 데스크톱: 2열 그리드 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="flex items-start">
                <div className="bg-blue-100 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">
                  <span className="text-blue-600 font-bold text-sm">1</span>
                </div>
                <div>
                  <h4 className="font-bold mb-1 text-sm sm:text-base">
                    서비스 초기 세팅
                  </h4>
                  <p className="text-xs sm:text-sm text-gray-600">
                    코드 설치부터 초기 설정까지 전문가가 직접 지원합니다.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-blue-100 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">
                  <span className="text-blue-600 font-bold text-sm">2</span>
                </div>
                <div>
                  <h4 className="font-bold mb-1 text-sm sm:text-base">
                    첫 테스트 설계
                  </h4>
                  <p className="text-xs sm:text-sm text-gray-600">
                    첫 A/B 테스트 및 히트맵 분석을 함께 설계해드립니다.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-blue-100 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">
                  <span className="text-blue-600 font-bold text-sm">3</span>
                </div>
                <div>
                  <h4 className="font-bold mb-1 text-sm sm:text-base">
                    팀 전체 교육
                  </h4>
                  <p className="text-xs sm:text-sm text-gray-600">
                    마케팅팀 전체가 사용할 수 있도록 교육을 제공합니다.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-blue-100 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">
                  <span className="text-blue-600 font-bold text-sm">4</span>
                </div>
                <div>
                  <h4 className="font-bold mb-1 text-sm sm:text-base">
                    밀착 지원
                  </h4>
                  <p className="text-xs sm:text-sm text-gray-600">
                    문제 발생 시 모든 채널로 즉시 해결해드립니다.
                  </p>
                </div>
              </div>
            </div>

            {/* 모바일 전용 - 추가 강조 메시지 */}
            <div className="sm:hidden mt-4 pt-4 border-t border-gray-200 text-center">
              <p className="text-xs text-gray-600">
                ✨ 설치부터 운영까지 100% 지원
              </p>
            </div>
          </div>
        </div>
      </section >


      {/* 무료 체험 문의 폼 섹션 */}
      < section
        className="py-20 bg-gradient-to-br from-blue-50 to-white"
        id="signup"
      >
        <div className="max-w-4xl mx-auto px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-3xl lg:text-4xl font-bold mb-4">
              <span className="text-blue-600">30초</span>만에 무료 체험 문의
            </h2>
            <p className="text-xl text-gray-600">
              담당자가 직접 연락드리겠습니다.
            </p>
          </div>

          {/* 👇 여기에 혜택 섹션 (폼 밖에!) */}
          <div className="mb-8 bg-white rounded-2xl shadow-lg p-6 lg:p-8 border-2 border-blue-100">
            <div className="text-center mb-6">
              <span className="text-3xl mb-2 block">✨</span>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">
                무료 체험에 포함된 혜택
              </h3>
              <p className="text-sm sm:text-base text-gray-600">
                지금 신청하시면 모두 무료로 제공됩니다
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-1">
              {[
                { icon: "📅", text: "30일 무료 체험 (카드 등록 X)" },
                { icon: "🚀", text: "모든 기능 무제한 사용" },
                { icon: "👨‍💼", text: "1:1 전담 매니저 배정" },
                { icon: "🔧", text: "무료 설치 및 설정 지원" },
                { icon: "💬", text: "실시간 기술 지원" },
                { icon: "✅", text: "체험 종료 후 자동 결제 없음" },
                { icon: "🎯", text: "A/B 테스트, 히트맵 전략 컨설팅" },
                { icon: "📊", text: "데이터 분석 및 인사이트 제공" },
              ].map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-2 sm:space-x-3 p-2 sm:p-3 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  <span className="text-xl sm:text-2xl flex-shrink-0">{benefit.icon}</span>
                  <span className="text-sm sm:text-base text-gray-700 font-medium pt-0.5 sm:pt-1">
                    {benefit.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 border border-blue-100">
            <form onSubmit={handleQuickFormSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* 이름 */}
                <div>
                  <label
                    htmlFor="quick-name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    이름 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="quick-name"
                    name="name"
                    value={quickForm.name}
                    onChange={handleQuickFormChange}
                    placeholder="이름을 입력해주세요"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                    required
                  />
                </div>

                {/* 회사명 */}
                <div>
                  <label
                    htmlFor="quick-company"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    회사명
                  </label>
                  <input
                    type="text"
                    id="quick-company"
                    name="company"
                    value={quickForm.company}
                    onChange={handleQuickFormChange}
                    placeholder="회사명을 입력해주세요"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                  />
                </div>
              </div>

              {/* 선호하는 연락 방법 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  선호하는 연락 방법 <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { value: "email", label: "이메일" },
                    { value: "phone", label: "전화" },
                    { value: "sms", label: "문자" },
                    { value: "kakao", label: "카카오톡" },
                  ].map((method) => (
                    <div key={method.value}>
                      <input
                        type="radio"
                        id={`contact-${method.value}`}
                        name="contactMethod"
                        value={method.value}
                        checked={quickForm.contactMethod === method.value}
                        onChange={handleQuickFormChange}
                        className="sr-only"
                      />
                      <label
                        htmlFor={`contact-${method.value}`}
                        className={`block text-center px-4 py-3 border rounded-lg cursor-pointer transition-all ${quickForm.contactMethod === method.value
                          ? "border-blue-500 bg-blue-50 text-blue-700 font-medium"
                          : "border-gray-300 bg-white text-gray-700 hover:border-blue-300"
                          }`}
                      >
                        {method.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* 연락처 */}
              <div>
                {quickForm.contactMethod === "email" ? (
                  <>
                    <label
                      htmlFor="quick-contact"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      이메일 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="quick-contact"
                      name="contact"
                      value={quickForm.contact}
                      onChange={handleQuickFormChange}
                      placeholder="example@company.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      required
                    />
                  </>
                ) : (
                  <>
                    <label
                      htmlFor="quick-contact"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      전화번호 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="quick-contact"
                      name="contact"
                      value={quickForm.contact}
                      onChange={handleQuickFormChange}
                      placeholder="010-1234-5678"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      required
                    />
                  </>
                )}
              </div>

              {/* 간단한 문의 */}
              <div>
                <label
                  htmlFor="quick-message"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  간단한 문의 (선택)
                </label>
                <textarea
                  id="quick-message"
                  name="message"
                  value={quickForm.message}
                  onChange={handleQuickFormChange}
                  rows="3"
                  placeholder="궁금한 점이나 요청사항을 간단히 적어주세요"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors resize-none"
                />
              </div>

              {/* 개인정보 동의 */}
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="quick-privacy"
                  name="privacy"
                  checked={quickForm.privacy}
                  onChange={handleQuickFormChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-1"
                  required
                />
                <label
                  htmlFor="quick-privacy"
                  className="ml-3 text-sm text-gray-600"
                >
                  <Link to="/privacy" className="text-blue-600 hover:underline">
                    개인정보처리방침
                  </Link>
                  에 동의합니다. <span className="text-red-500">*</span>
                </label>
              </div>

              {/* 제출 버튼 */}
              <div className="text-center pt-4">
                <button
                  type="submit"
                  disabled={
                    !quickForm.name ||
                    !quickForm.contact ||
                    !quickForm.privacy ||
                    isLoading
                  }
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      처리중...
                    </div>
                  ) : (
                    "무료 체험 문의하기"
                  )}
                </button>
                <p className="text-sm text-gray-500 mt-3">
                  평균 1시간 내 연락 • 맞춤 상담 제공
                </p>
              </div>
            </form>
          </div>

          {/* 추가 안내 */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center px-4 py-2 bg-green-50 text-green-800 rounded-full text-sm">
              <svg
                className="w-4 h-4 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              빠른 응답 보장 • 무료 설치 지원
            </div>
          </div>

          {/* 문의 링크 */}
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">다른 방법으로 문의하시겠어요?</p>
            <div className="flex justify-center gap-3 sm:gap-4">
              <a
                href="http://pf.kakao.com/_yixgxjn/chat"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 sm:px-6 py-2.5 sm:py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base"
              >
                <span className="text-yellow-500 mr-1.5 sm:mr-2">💬</span>
                카카오톡 문의
              </a>
              <a
                href="https://switchuplab.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 sm:px-6 py-2.5 sm:py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base"
              >
                홈페이지 방문
              </a>
            </div>
          </div>
        </div >
      </section >

      {/* 푸터 */}
      < Footer />
      <FloatingDownloadBanner />
    </div >
  );
};

export default FreeStartLanding;